import type {
  LscScheduleBindingCandidateMatch,
  LscScheduleBindingStatus,
  LscTruthScheduleBinding,
  LscVodCandidate,
  LscVodMatchSuggestion,
} from './types';
import { formatAwayAtHome } from './leagueNav';
import { displaySeasonLabel } from './seasonDisplay';
import { isVodCandidateUnavailable } from './vodAvailability';
import {
  CHECK_SCOREBOARD_BUTTON_LABEL,
  CONFIRM_RIGHT_GAME_LABEL,
  EMPTY_HUD_HANDOFF_PUBLISH_BLOCKED,
  EMPTY_HUD_NEXT_ACTION_DESCRIPTION,
  LEAGUE_MATCH_NEEDS_MATCH_CHIP,
  VERIFIED_LIVE_HANDOFF_CONFIRM_CTA,
  VERIFIED_LIVE_HANDOFF_CONFIRM_DESCRIPTION,
  VERIFIED_LIVE_HANDOFF_CONFIRM_TITLE,
  VERIFIED_LIVE_HANDOFF_BANNER_TITLE,
  VERIFIED_LIVE_HANDOFF_PUBLISH_BLOCKED,
  VERIFIED_LIVE_HANDOFF_SUMMARY,
  VERIFIED_LIVE_HANDOFF_VERIFY_STEP,
  VERIFIED_HANDOFF_MATCH_FALLBACK,
  VOD_TRUTH_VERIFICATION_QUEUE_LABEL,
} from './commissionerReviewCopy';
import { truthVerificationInProgress } from './vodTruthVerificationPolling';

export {
  CHECK_SCOREBOARD_BUTTON_LABEL,
  CONFIRM_RIGHT_GAME_LABEL,
  FRAME_EVIDENCE_COLLAPSE_LABEL,
  FRAME_EVIDENCE_EXPAND_LABEL,
  METADATA_MATCHER_ADVANCED_HELPER,
  METADATA_MATCHER_ADVANCED_LABEL,
  METADATA_MATCHER_BUTTON_LABEL,
  METADATA_MATCHER_HELPER_COPY,
  METADATA_MATCHER_RUNNING_LABEL,
  METADATA_MATCHER_UNVERIFIED_WARNING,
  TRUTH_REVIEW_LOCKED_COPY,
  TRUTH_VERIFICATION_COPY,
} from './commissionerReviewCopy';

export type LscTruthStatus =
  | 'unverified'
  | 'verified'
  | 'likely'
  | 'inconclusive'
  | 'conflict';

export type LscGameplayVerificationStatus =
  | 'gameplay_detected'
  | 'no_gameplay_detected'
  | 'inconclusive';

export type LscLeagueMatchResolution =
  | 'matched'
  | 'likely_match'
  | 'possible_team_match'
  | 'no_league_match'
  | 'ambiguous'
  | 'conflict'
  | 'cross_league_identity_conflict'
  | 'needs_review'
  | 'current_season_schedule_miss'
  | 'not_checked';

export type LscTruthRecommendedAction =
  | 'publish_after_review'
  | 'assign_manually'
  | 'ignore_candidate'
  | 'review_required'
  | 'do_not_publish';


const TRUTH_BADGE_LABELS: Record<LscTruthStatus, string> = {
  unverified: 'Unverified',
  verified: 'Verified',
  likely: 'Likely',
  inconclusive: 'Inconclusive',
  conflict: 'Conflict',
};

const TRUTH_BADGE_STYLES: Record<LscTruthStatus, string> = {
  unverified: 'bg-secondary-700 text-secondary-200 border-secondary-600',
  verified: 'bg-green-900/50 text-green-200 border-green-700',
  likely: 'bg-primary-900/40 text-primary-200 border-primary-700',
  inconclusive: 'bg-amber-900/50 text-amber-200 border-amber-700',
  conflict: 'bg-red-900/50 text-red-200 border-red-800',
};

type TruthEvidence = Record<string, unknown>;

function truthEvidenceObject(candidate: LscVodCandidate): TruthEvidence | null {
  const evidence = candidate.truth_evidence;
  if (!evidence || typeof evidence !== 'object') {
    return null;
  }
  return evidence as TruthEvidence;
}

export function truthStatusFromCandidate(
  candidate: Pick<LscVodCandidate, 'truth_status'>
): LscTruthStatus {
  const status = (candidate.truth_status || 'unverified').toLowerCase();
  if (status in TRUTH_BADGE_LABELS) {
    return status as LscTruthStatus;
  }
  return 'unverified';
}

export function truthBadgeLabel(status: LscTruthStatus): string {
  return TRUTH_BADGE_LABELS[status];
}

export function truthBadgeStyle(status: LscTruthStatus): string {
  return TRUTH_BADGE_STYLES[status];
}

export function truthShowsVerifyButton(
  candidate: Pick<LscVodCandidate, 'truth_checked_at' | 'review_status'>
): boolean {
  return candidate.review_status !== 'published';
}


export function truthReviewActionsLocked(
  candidate: Pick<LscVodCandidate, 'truth_checked_at' | 'review_status'>
): boolean {
  if (candidate.review_status === 'published') {
    return false;
  }
  return !candidate.truth_checked_at;
}

export function truthReviewActionsUnlocked(
  candidate: Pick<LscVodCandidate, 'truth_checked_at' | 'review_status'>
): boolean {
  return !truthReviewActionsLocked(candidate);
}

export function truthCanForceReverify(candidate: Pick<LscVodCandidate, 'truth_checked_at'>): boolean {
  return Boolean(candidate.truth_checked_at);
}

export function truthActiveGamerTags(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const active = evidence?.active_game_gamer_tags;
  if (Array.isArray(active) && active.length > 0) {
    return active.map((tag) => String(tag));
  }
  return candidate.truth_detected_gamer_tags ?? [];
}

export function truthIgnoredOnFieldPlayerNames(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const names = evidence?.ignored_on_field_player_names;
  if (!Array.isArray(names)) {
    return [];
  }
  return names.map((name) => String(name)).filter(Boolean);
}

export function truthIgnoredPlaycallOverlayNames(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const names = evidence?.ignored_playcall_overlay_names;
  if (!Array.isArray(names)) {
    return [];
  }
  return names.map((name) => String(name)).filter(Boolean);
}

export function truthTopHudGamerTags(candidate: LscVodCandidate): string[] {
  return truthActiveGamerTags(candidate);
}

export function truthIgnoredOverlayTags(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const ticker = evidence?.ticker_or_non_active_gamer_tags;
  if (!Array.isArray(ticker)) {
    return [];
  }
  return ticker.map((tag) => String(tag)).filter(Boolean);
}

export function truthTeamAbbreviations(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const abbreviations = evidence?.team_abbreviations_detected;
  if (!Array.isArray(abbreviations)) {
    return [];
  }
  return abbreviations.map((abbr) => String(abbr)).filter(Boolean);
}

export function truthFrameSummary(candidate: LscVodCandidate): {
  usable: number;
  ignored: number;
  total: number;
} | null {
  const evidence = truthEvidenceObject(candidate);
  if (!evidence) return null;
  const usable = Number(evidence.usable_gameplay_frames ?? 0);
  const ignored = Number(evidence.ignored_menu_frames ?? 0);
  const total = usable + ignored;
  if (!total && !usable) return null;
  return { usable, ignored, total: total || usable + ignored };
}

export function truthResolutionNotes(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const resolution = evidence?.resolution;
  if (!resolution || typeof resolution !== 'object') return [];
  const notes = (resolution as Record<string, unknown>).resolution_notes;
  if (!Array.isArray(notes)) return [];
  return notes.map((note) => String(note)).filter(Boolean);
}

export function truthAliasNotes(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const resolution = evidence?.resolution;
  if (!resolution || typeof resolution !== 'object') return [];
  const notes = (resolution as Record<string, unknown>).alias_resolution_notes;
  if (!Array.isArray(notes)) return [];
  return notes.map((note) => String(note)).filter(Boolean);
}

export function truthIgnoredMenuNames(candidate: LscVodCandidate): string[] {
  const evidence = truthEvidenceObject(candidate);
  const menu = evidence?.ignored_menu_or_profile_names;
  if (Array.isArray(menu) && menu.length > 0) {
    return menu.map((name) => String(name)).filter(Boolean);
  }
  return truthIgnoredOverlayTags(candidate);
}

const FRAME_VERIFICATION_LABELS: Record<string, string> = {
  verified: 'Gameplay detected',
  likely: 'Gameplay likely',
  inconclusive: 'Inconclusive',
  conflict: 'Conflict',
  unverified: 'Unverified',
};

const LEAGUE_MATCH_LABELS: Record<string, string> = {
  matched: 'League match verified',
  likely_match: 'Needs match review',
  possible_team_match: 'Detected teams need confirmation',
  no_league_match: 'No league match found',
  ambiguous: 'Multiple possible league games',
  conflict: 'Assigned match contradicts detected teams',
  cross_league_identity_conflict: 'Players may belong to another league',
  needs_review: 'Insufficient scorebug evidence — review required',
  current_season_schedule_miss: 'Detected teams not in current season schedule',
  not_checked: 'Not checked',
};

const RECOMMENDED_ACTION_LABELS: Record<string, string> = {
  publish_after_review: 'Review and publish',
  assign_manually: 'Assign manually',
  ignore_candidate: 'Ignore candidate',
  review_required: 'Review required',
  do_not_publish: 'Do not publish',
};

const RECOMMENDED_ACTION_GUIDANCE: Record<string, string> = {
  publish_after_review:
    'Recommended: Review and publish after confirming top-HUD gamer tags and teams match league members.',
  assign_manually: 'Recommended: Assign or confirm manually.',
  ignore_candidate:
    'Recommended: Ignore this candidate. Frame verification detected gameplay, but no league match was found.',
  review_required:
    'Recommended: Review the detected teams against the assigned or suggested match before publishing.',
  do_not_publish:
    'Recommended: Do not publish. Detected gamer tags do not match this league’s assigned team owners.',
};

function readTruthField(candidate: LscVodCandidate, key: string): string | null {
  const topLevel = (candidate as unknown as Record<string, unknown>)[key];
  if (typeof topLevel === 'string' && topLevel.trim()) {
    return topLevel.trim();
  }
  const evidence = truthEvidenceObject(candidate);
  const nested = evidence?.[key];
  if (typeof nested === 'string' && nested.trim()) {
    return nested.trim();
  }
  const resolution = evidence?.resolution;
  if (resolution && typeof resolution === 'object') {
    const fromResolution = (resolution as Record<string, unknown>)[key];
    if (typeof fromResolution === 'string' && fromResolution.trim()) {
      return fromResolution.trim();
    }
  }
  return null;
}

export function truthGameplayVerificationStatus(
  candidate: LscVodCandidate
): LscGameplayVerificationStatus {
  const value =
    readTruthField(candidate, 'gameplay_verification_status') ??
    readTruthField(candidate, 'truth_gameplay_verification_status');
  if (value === 'gameplay_detected' || value === 'no_gameplay_detected') {
    return value;
  }
  const evidence = truthEvidenceObject(candidate);
  if (evidence?.gameplay_detected === true) {
    return 'gameplay_detected';
  }
  return 'inconclusive';
}

export function truthGameplayVerificationLabel(candidate: LscVodCandidate): string {
  const status = truthGameplayVerificationStatus(candidate);
  if (status === 'gameplay_detected') return 'Gameplay detected';
  if (status === 'no_gameplay_detected') return 'No gameplay detected';
  return 'Inconclusive';
}

export function truthLeagueMatchResolution(
  candidate: LscVodCandidate
): LscLeagueMatchResolution {
  const value =
    readTruthField(candidate, 'league_match_resolution') ??
    readTruthField(candidate, 'truth_league_match_resolution');
  if (value && value in LEAGUE_MATCH_LABELS) {
    return value as LscLeagueMatchResolution;
  }
  return 'not_checked';
}

export function truthLeagueMatchLabel(candidate: LscVodCandidate): string {
  return LEAGUE_MATCH_LABELS[truthLeagueMatchResolution(candidate)] ?? 'Not checked';
}

export function truthRecommendedAction(
  candidate: LscVodCandidate
): LscTruthRecommendedAction | null {
  const value =
    readTruthField(candidate, 'recommended_action') ??
    readTruthField(candidate, 'truth_recommended_action');
  if (value && value in RECOMMENDED_ACTION_LABELS) {
    return value as LscTruthRecommendedAction;
  }
  return null;
}

export function truthRecommendedActionLabel(candidate: LscVodCandidate): string | null {
  const action = truthRecommendedAction(candidate);
  return action ? RECOMMENDED_ACTION_LABELS[action] : null;
}

export function truthRecommendedActionGuidance(candidate: LscVodCandidate): string | null {
  const action = truthRecommendedAction(candidate);
  return action ? RECOMMENDED_ACTION_GUIDANCE[action] : null;
}

export function truthLeagueResolutionSummary(candidate: LscVodCandidate): string | null {
  const summary =
    readTruthField(candidate, 'league_resolution_summary') ??
    readTruthField(candidate, 'truth_league_resolution_summary');
  return summary;
}

export function truthFrameVerificationLabel(candidate: LscVodCandidate): string {
  if (!candidate.truth_checked_at) {
    return FRAME_VERIFICATION_LABELS.unverified;
  }
  const gameplayStatus = truthGameplayVerificationStatus(candidate);
  if (gameplayStatus === 'gameplay_detected') {
    const status = truthStatusFromCandidate(candidate);
    if (status === 'verified') return 'Gameplay detected';
    if (status === 'likely') return 'Gameplay likely';
  }
  if (gameplayStatus === 'no_gameplay_detected') return 'No gameplay detected';
  const status = truthStatusFromCandidate(candidate);
  return FRAME_VERIFICATION_LABELS[status] ?? 'Inconclusive';
}

const LEAGUE_MATCH_STYLES: Record<LscLeagueMatchResolution, string> = {
  matched: 'bg-green-900/50 text-green-200 border-green-700',
  likely_match: 'bg-amber-900/50 text-amber-200 border-amber-700',
  possible_team_match: 'bg-amber-900/50 text-amber-200 border-amber-700',
  no_league_match: 'bg-amber-900/50 text-amber-200 border-amber-700',
  ambiguous: 'bg-amber-900/50 text-amber-200 border-amber-700',
  conflict: 'bg-red-900/50 text-red-200 border-red-800',
  cross_league_identity_conflict: 'bg-red-900/50 text-red-200 border-red-800',
  needs_review: 'bg-amber-900/50 text-amber-200 border-amber-700',
  current_season_schedule_miss: 'bg-amber-900/50 text-amber-200 border-amber-700',
  not_checked: 'bg-secondary-700 text-secondary-200 border-secondary-600',
};

const PUBLISH_BLOCKED_RESOLUTIONS = new Set<LscLeagueMatchResolution>([
  'no_league_match',
  'cross_league_identity_conflict',
  'ambiguous',
  'possible_team_match',
  'needs_review',
  'current_season_schedule_miss',
]);

const OVERRIDABLE_HUD_STATUSES = new Set([
  'identity_unverified',
  'likely_identity_match',
]);

export interface TruthCommissionerIdentityOverride {
  raw_tag?: string;
  detected_side?: string;
  detected_side_label?: string;
  member_id?: string;
  member_display_name?: string;
  member_in_game_name?: string;
  member_status?: string;
  created_at?: string;
  note?: string | null;
  created_by_display_name?: string;
  stale?: boolean;
  stale_reason?: string;
  stale_at?: string;
}

export function truthCommissionerIdentityOverrides(
  candidate: LscVodCandidate
): TruthCommissionerIdentityOverride[] {
  const evidence = truthEvidenceObject(candidate);
  const overrides = evidence?.commissioner_identity_overrides;
  if (!Array.isArray(overrides)) {
    return [];
  }
  return overrides as TruthCommissionerIdentityOverride[];
}

export function truthActiveCommissionerIdentityOverrides(
  candidate: LscVodCandidate
): TruthCommissionerIdentityOverride[] {
  return truthCommissionerIdentityOverrides(candidate).filter((item) => !item.stale);
}

export function truthHasCommissionerIdentityOverrides(candidate: LscVodCandidate): boolean {
  return truthActiveCommissionerIdentityOverrides(candidate).length > 0;
}

export function truthIdentityOverrideSummary(candidate: LscVodCandidate): string | null {
  const overrides = truthActiveCommissionerIdentityOverrides(candidate);
  if (overrides.length === 0) {
    return null;
  }
  const first = overrides[0];
  const raw = String(first.raw_tag ?? '').trim();
  const display = String(first.member_display_name ?? '').trim();
  const inGame = String(first.member_in_game_name ?? '').trim();
  const side = String(first.detected_side_label ?? first.detected_side ?? '').trim();
  if (!raw || !display) {
    return null;
  }
  const memberLabel = inGame ? `${display} · ${inGame} · ${side}` : `${display} · ${side}`;
  return `Verified by commissioner\n${raw} was linked to ${memberLabel} for this VOD.`;
}

export function truthOverridableIdentityTags(
  candidate: LscVodCandidate
): TruthHudTagNormalization[] {
  const overrides = new Set(
    truthActiveCommissionerIdentityOverrides(candidate).map(
      (item) => `${String(item.raw_tag ?? '')}::${String(item.detected_side ?? '')}`
    )
  );
  return truthHudTagNormalizations(candidate).filter((item) => {
    const status = String(item.status ?? '');
    if (!OVERRIDABLE_HUD_STATUSES.has(status)) {
      return false;
    }
    const key = `${String(item.raw_tag ?? '')}::${String(item.detected_side ?? '')}`;
    return !overrides.has(key);
  });
}

export function truthIsOverrideEligible(candidate: LscVodCandidate): boolean {
  if (!candidate.truth_checked_at) {
    return false;
  }
  if (candidate.truth_gameplay_verification_status !== 'gameplay_detected') {
    return false;
  }
  if (!candidate.competition_match_id) {
    return false;
  }
  if (candidate.review_status === 'published') {
    return false;
  }
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict' || resolution === 'no_league_match') {
    return false;
  }
  if (resolution === 'ambiguous' && !candidate.competition_match_id) {
    return false;
  }
  return truthOverridableIdentityTags(candidate).length > 0;
}

export interface TruthEmptyHudHandoffAttestation {
  attestation_type?: string;
  scope?: string;
  competition_match_id?: string;
  match_source?: string;
  handoff_suggestion_id?: string;
  provider_broadcaster_id?: string;
  provider_broadcaster_login?: string;
  member_id?: string;
  detected_teams?: string[];
  truth_bound_match_id?: string;
  created_at?: string;
  note?: string | null;
  created_by_display_name?: string;
  reason_code?: string;
  stale?: boolean;
  stale_reason?: string;
  stale_at?: string;
}

export function truthEmptyHudHandoffAttestations(
  candidate: LscVodCandidate
): TruthEmptyHudHandoffAttestation[] {
  const evidence = truthEvidenceObject(candidate);
  const attestations = evidence?.commissioner_empty_hud_handoff_attestations;
  if (!Array.isArray(attestations)) {
    return [];
  }
  return attestations as TruthEmptyHudHandoffAttestation[];
}

export function truthActiveEmptyHudHandoffAttestations(
  candidate: LscVodCandidate
): TruthEmptyHudHandoffAttestation[] {
  return truthEmptyHudHandoffAttestations(candidate).filter((item) => !item.stale);
}

export function truthHasActiveEmptyHudHandoffAttestation(candidate: LscVodCandidate): boolean {
  return truthActiveEmptyHudHandoffAttestations(candidate).length > 0;
}

export function truthEmptyHudHandoffAttestationSummary(candidate: LscVodCandidate): string | null {
  const attestations = truthActiveEmptyHudHandoffAttestations(candidate);
  if (attestations.length === 0) {
    return null;
  }
  const first = attestations[0];
  const broadcaster = String(first.provider_broadcaster_login ?? '').trim();
  const matchId = String(first.competition_match_id ?? '').trim();
  const teams = (first.detected_teams ?? []).join(' vs ');
  const parts = ['Verified by commissioner for verified handoff match'];
  if (broadcaster) {
    parts.push(`Broadcaster: ${broadcaster}`);
  }
  if (teams) {
    parts.push(`Teams: ${teams}`);
  }
  if (matchId) {
    parts.push(`Match: ${matchId}`);
  }
  return parts.join('\n');
}

export function truthIsEmptyHudHandoffAttestationEligible(
  candidate: LscVodCandidate
): boolean {
  if (!candidate.truth_checked_at) {
    return false;
  }
  if (!isVerifiedLiveHandoffCandidate(candidate)) {
    return false;
  }
  if (candidate.review_status !== 'approved') {
    return false;
  }
  if (candidate.truth_gameplay_verification_status !== 'gameplay_detected') {
    return false;
  }
  if (candidate.truth_schedule_binding_status !== 'bound') {
    return false;
  }
  if (!candidate.competition_match_id) {
    return false;
  }
  if (candidate.truth_bound_match_id !== candidate.competition_match_id) {
    return false;
  }
  if (truthLeagueMatchResolution(candidate) !== 'possible_team_match') {
    return false;
  }
  if (candidate.archive_id || candidate.published_at_lsc) {
    return false;
  }
  if (truthLeagueMatchResolution(candidate) === 'cross_league_identity_conflict') {
    return false;
  }
  if (truthHasActiveEmptyHudHandoffAttestation(candidate)) {
    return false;
  }
  if (truthHudTagNormalizations(candidate).length > 0) {
    return false;
  }
  if (truthOverridableIdentityTags(candidate).length > 0) {
    return false;
  }
  return true;
}

export function truthIdentitySatisfiedWithCommissionerOverrides(
  candidate: LscVodCandidate
): boolean {
  const normalizations = truthHudTagNormalizations(candidate);
  if (normalizations.length === 0) {
    return false;
  }
  const overrideKeys = new Set(
    truthActiveCommissionerIdentityOverrides(candidate).map(
      (item) => `${String(item.raw_tag ?? '')}::${String(item.detected_side ?? '')}`
    )
  );
  for (const norm of normalizations) {
    const status = String(norm.status ?? '');
    if (status === 'exact_identity_match' || status === 'normalized_identity_match') {
      continue;
    }
    if (status === 'ambiguous_identity_match') {
      return false;
    }
    const key = `${String(norm.raw_tag ?? '')}::${String(norm.detected_side ?? '')}`;
    if (!overrideKeys.has(key)) {
      return false;
    }
  }
  return true;
}

function labelMatchesScheduleSide(
  side: OrientedSide | null,
  scheduleLabel: string | null | undefined
): boolean {
  if (!side || !scheduleLabel) {
    return false;
  }
  const label = scheduleLabel.trim().toLowerCase();
  const candidates = [side.team_name, side.team_abbreviation]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
  return candidates.some(
    (value) => value === label || value.includes(label) || label.includes(value)
  );
}

function scheduleMatchOrientedAgainstVod(
  candidate: LscVodCandidate,
  match: Pick<LscScheduleBindingCandidateMatch, 'away_side_label' | 'home_side_label'>
): boolean {
  return (
    labelMatchesScheduleSide(truthDetectedAwaySide(candidate), match.away_side_label) &&
    labelMatchesScheduleSide(truthDetectedHomeSide(candidate), match.home_side_label)
  );
}

function scheduleMatchReversedAgainstVod(
  candidate: LscVodCandidate,
  match: Pick<LscScheduleBindingCandidateMatch, 'away_side_label' | 'home_side_label'>
): boolean {
  return (
    labelMatchesScheduleSide(truthDetectedAwaySide(candidate), match.home_side_label) &&
    labelMatchesScheduleSide(truthDetectedHomeSide(candidate), match.away_side_label)
  );
}

export function truthAssignedScheduleMatchRow(
  candidate: LscVodCandidate
): LscScheduleBindingCandidateMatch | null {
  const assignedId = candidate.competition_match_id;
  if (!assignedId) {
    return null;
  }
  return (
    truthScheduleCandidateMatches(candidate).find((match) => match.match_id === assignedId) ??
    null
  );
}

export function truthPublishIdentityClear(candidate: LscVodCandidate): boolean {
  const resolution = truthLeagueMatchResolution(candidate);
  if (
    resolution === 'no_league_match' ||
    resolution === 'cross_league_identity_conflict' ||
    resolution === 'needs_review' ||
    resolution === 'current_season_schedule_miss'
  ) {
    return false;
  }
  if (resolution === 'possible_team_match') {
    return truthIdentitySatisfiedWithCommissionerOverrides(candidate);
  }
  if (truthOverridableIdentityTags(candidate).length > 0) {
    return truthIdentitySatisfiedWithCommissionerOverrides(candidate);
  }
  return true;
}

export function truthAssignedMatchResolvesScheduleAmbiguity(
  candidate: LscVodCandidate
): boolean {
  if (!candidate.competition_match_id) {
    return false;
  }
  if (truthLeagueMatchResolution(candidate) !== 'ambiguous') {
    return false;
  }
  if (candidate.truth_gameplay_verification_status !== 'gameplay_detected') {
    return false;
  }
  if (truthHomeAwayConfidence(candidate) !== 'high') {
    return false;
  }
  if (!truthPublishIdentityClear(candidate)) {
    return false;
  }
  const assignedRow = truthAssignedScheduleMatchRow(candidate);
  if (assignedRow) {
    if (scheduleMatchReversedAgainstVod(candidate, assignedRow)) {
      return false;
    }
    return scheduleMatchOrientedAgainstVod(candidate, assignedRow);
  }
  return true;
}

export const ASSIGNED_MATCH_READY_MESSAGE =
  'Assigned match matches detected home/away teams. Ready for commissioner publish review.';

export function truthAssignedMatchReadyMessage(candidate: LscVodCandidate): string | null {
  if (!truthAssignedMatchResolvesScheduleAmbiguity(candidate)) {
    return null;
  }
  return ASSIGNED_MATCH_READY_MESSAGE;
}

export function truthPublishBlocked(candidate: LscVodCandidate): boolean {
  if (candidate.commissioner_publish) {
    const reason = candidate.commissioner_publish.reason;
    if (reason && reason !== 'Confirm this match before publishing.') {
      return true;
    }
    return !candidate.commissioner_publish.teams_match;
  }
  if (!candidate.truth_checked_at) {
    return true;
  }
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict') {
    return true;
  }
  if (resolution === 'no_league_match') {
    return true;
  }
  if (resolution === 'needs_review' || resolution === 'current_season_schedule_miss') {
    return true;
  }
  if (resolution === 'ambiguous' && !candidate.competition_match_id) {
    return true;
  }
  if (resolution === 'ambiguous' && candidate.competition_match_id) {
    if (candidate.truth_gameplay_verification_status !== 'gameplay_detected') {
      return true;
    }
    if (!truthPublishIdentityClear(candidate)) {
      return true;
    }
    return !truthAssignedMatchResolvesScheduleAmbiguity(candidate);
  }
  if (resolution === 'possible_team_match') {
    if (candidate.truth_gameplay_verification_status !== 'gameplay_detected') {
      return true;
    }
    if (truthIdentitySatisfiedWithCommissionerOverrides(candidate)) {
      return false;
    }
    if (truthHasActiveEmptyHudHandoffAttestation(candidate)) {
      return false;
    }
    return true;
  }
  return PUBLISH_BLOCKED_RESOLUTIONS.has(resolution);
}

export function truthAssignBlocked(candidate: LscVodCandidate): boolean {
  return truthLeagueMatchResolution(candidate) === 'cross_league_identity_conflict';
}

export const VERIFIED_LIVE_HANDOFF_MATCH_SOURCE = 'verified_live_handoff';

export function isVerifiedLiveHandoffCandidate(candidate: LscVodCandidate): boolean {
  return String(candidate.match_source ?? '').trim() === VERIFIED_LIVE_HANDOFF_MATCH_SOURCE;
}

const VOD_HANDOFF_SYNC_SOURCE_LABELS: Record<string, string> = {
  vod_handoff_automation: 'VOD handoff automation',
};

const VOD_MATCH_SOURCE_LABELS: Record<string, string> = {
  verified_live_handoff: 'Verified live handoff',
};

export function formatVodHandoffSourceLabel(value?: string | null): string {
  const key = String(value ?? '').trim();
  if (!key) {
    return '—';
  }
  return VOD_HANDOFF_SYNC_SOURCE_LABELS[key] ?? key.replace(/_/g, ' ');
}

export function formatVodMatchSourceLabel(value?: string | null): string {
  const key = String(value ?? '').trim();
  if (!key) {
    return '—';
  }
  return VOD_MATCH_SOURCE_LABELS[key] ?? key.replace(/_/g, ' ');
}

function verifiedHandoffStreamId(candidate: LscVodCandidate): string | null {
  const streamId = candidate.source_metadata?.stream_id;
  if (streamId == null) {
    return null;
  }
  const normalized = String(streamId).trim();
  return normalized || null;
}

export function formatCompetitionMatchId(competitionMatchId?: string | null): string | null {
  const normalized = String(competitionMatchId ?? '').trim();
  if (!normalized) {
    return null;
  }
  const extMatch = normalized.match(/:ext:([^:]+)$/);
  if (extMatch?.[1]) {
    return `ext:${extMatch[1]}`;
  }
  const tail = normalized.split(':').pop();
  return tail?.trim() || null;
}

function readHandoffSourceMetadataString(
  candidate: LscVodCandidate,
  keys: string[]
): string | null {
  const metadata = candidate.source_metadata;
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function handoffTeamMatchupFromMetadata(candidate: LscVodCandidate): string | null {
  const away = readHandoffSourceMetadataString(candidate, ['away_team', 'away_side_label']);
  const home = readHandoffSourceMetadataString(candidate, ['home_team', 'home_side_label']);
  if (!away && !home) {
    return null;
  }
  return formatAwayAtHome({
    away_side_label: away ?? undefined,
    home_side_label: home ?? undefined,
  });
}

function handoffMatchSummaryFromMetadata(candidate: LscVodCandidate): string | null {
  const metadata = candidate.source_metadata;
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const summary = metadata.match_summary;
  if (!summary || typeof summary !== 'object') {
    return null;
  }
  const record = summary as { away_side_label?: string; home_side_label?: string };
  if (!record.away_side_label?.trim() && !record.home_side_label?.trim()) {
    return null;
  }
  return formatAwayAtHome({
    away_side_label: record.away_side_label,
    home_side_label: record.home_side_label,
  });
}

export type VerifiedHandoffMatchLabel = {
  label: string;
  prefix: 'Match' | 'Match ID';
};

export function getVerifiedHandoffMatchLabel(
  candidate: LscVodCandidate
): VerifiedHandoffMatchLabel | null {
  if (!isVerifiedLiveHandoffCandidate(candidate)) {
    return null;
  }

  const timeline = truthTimelineLabel(candidate);
  if (timeline) {
    return { label: timeline, prefix: 'Match' };
  }

  const metadataScheduleLabel = readHandoffSourceMetadataString(candidate, [
    'match_label',
    'timeline_label',
    'competition_match_label',
    'assigned_match_label',
  ]);
  if (metadataScheduleLabel) {
    return { label: metadataScheduleLabel, prefix: 'Match' };
  }

  const summaryLabel = handoffMatchSummaryFromMetadata(candidate);
  if (summaryLabel) {
    return { label: summaryLabel, prefix: 'Match' };
  }

  const assignedRow = truthAssignedScheduleMatchRow(candidate);
  if (assignedRow) {
    const assignedLabel = formatScheduleCandidateMatch(assignedRow).trim();
    if (assignedLabel) {
      return { label: assignedLabel, prefix: 'Match' };
    }
  }

  const metadataTeams = handoffTeamMatchupFromMetadata(candidate);
  if (metadataTeams) {
    return { label: metadataTeams, prefix: 'Match' };
  }

  if (
    candidate.competition_match_id &&
    truthScheduleBindingStatus(candidate) === 'bound'
  ) {
    const boundMatchup = formatDetectedVodMatchup(candidate);
    if (boundMatchup) {
      return { label: boundMatchup, prefix: 'Match' };
    }
  }

  const competitionMatchId = formatCompetitionMatchId(candidate.competition_match_id);
  if (competitionMatchId) {
    return { label: competitionMatchId, prefix: 'Match ID' };
  }

  return { label: VERIFIED_HANDOFF_MATCH_FALLBACK, prefix: 'Match' };
}

export type VerifiedHandoffProvenanceBadge = {
  key: string;
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
};

export type VerifiedHandoffProvenance = {
  isVerifiedLiveHandoff: boolean;
  badges: VerifiedHandoffProvenanceBadge[];
  summaryLine: string | null;
  bannerTitle: string | null;
  bannerHelper: string | null;
  liveStreamId: string | null;
  twitchVodId: string | null;
  matchLabel: string | null;
  matchLabelPrefix: 'Match' | 'Match ID';
  publishBlockedNote: string | null;
  syncSourceLabel: string | null;
  matchSourceLabel: string | null;
};

export function getVerifiedHandoffProvenance(candidate: LscVodCandidate): VerifiedHandoffProvenance {
  const isVerifiedLiveHandoff = isVerifiedLiveHandoffCandidate(candidate);
  if (!isVerifiedLiveHandoff) {
    return {
      isVerifiedLiveHandoff: false,
      badges: [],
      summaryLine: null,
      bannerTitle: null,
      bannerHelper: null,
      liveStreamId: null,
      twitchVodId: null,
      matchLabel: null,
      matchLabelPrefix: 'Match',
      publishBlockedNote: null,
      syncSourceLabel: null,
      matchSourceLabel: null,
    };
  }

  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();
  const liveStreamId = verifiedHandoffStreamId(candidate);
  const twitchVodId = candidate.provider_vod_id?.trim() || null;
  const badges: VerifiedHandoffProvenanceBadge[] = [
    { key: 'verified_live_handoff', label: 'Verified live handoff', tone: 'success' },
  ];
  if (twitchVodId) {
    badges.push({ key: 'vod_linked', label: 'VOD linked', tone: 'success' });
  }
  if (reviewStatus === 'pending_review') {
    badges.push({
      key: 'pending_commissioner_review',
      label: 'Pending commissioner review',
      tone: 'warning',
    });
  }

  const publishBlockedNote =
    reviewStatus !== 'published' ? VERIFIED_LIVE_HANDOFF_PUBLISH_BLOCKED : null;
  const match = getVerifiedHandoffMatchLabel(candidate);

  return {
    isVerifiedLiveHandoff: true,
    badges,
    summaryLine: VERIFIED_LIVE_HANDOFF_SUMMARY,
    bannerTitle: VERIFIED_LIVE_HANDOFF_BANNER_TITLE,
    bannerHelper: VERIFIED_LIVE_HANDOFF_SUMMARY,
    liveStreamId,
    twitchVodId,
    matchLabel: match?.label ?? null,
    matchLabelPrefix: match?.prefix ?? 'Match',
    publishBlockedNote,
    syncSourceLabel: formatVodHandoffSourceLabel(candidate.sync_source),
    matchSourceLabel: formatVodMatchSourceLabel(candidate.match_source),
  };
}

export type VerifiedHandoffReviewContext = {
  nextActionTitle: string;
  nextActionDescription: string;
  verifyStepDescription: string;
  primaryLabel: string;
};

export function getVerifiedHandoffReviewContext(
  candidate: LscVodCandidate
): VerifiedHandoffReviewContext | null {
  if (!isVerifiedLiveHandoffCandidate(candidate) || candidate.truth_checked_at) {
    return null;
  }
  return {
    nextActionTitle: VERIFIED_LIVE_HANDOFF_CONFIRM_TITLE,
    nextActionDescription: VERIFIED_LIVE_HANDOFF_CONFIRM_DESCRIPTION,
    verifyStepDescription: VERIFIED_LIVE_HANDOFF_VERIFY_STEP,
    primaryLabel: VERIFIED_LIVE_HANDOFF_CONFIRM_CTA,
  };
}

export function canApproveHandoffLinkedCandidate(candidate: LscVodCandidate): boolean {
  return (
    isVerifiedLiveHandoffCandidate(candidate) &&
    Boolean(candidate.competition_match_id) &&
    Boolean(candidate.truth_checked_at) &&
    !truthReviewActionsLocked(candidate) &&
    !truthAssignBlocked(candidate)
  );
}

export function truthCrossLeagueConflictMessage(candidate: LscVodCandidate): string | null {
  if (truthLeagueMatchResolution(candidate) !== 'cross_league_identity_conflict') {
    return null;
  }
  return (
    truthLeagueResolutionSummary(candidate) ??
    'The detected gamer tags do not match the assigned team owners in this league. This VOD may belong to another league you manage.'
  );
}

export function truthPublishBlockedMessage(candidate: LscVodCandidate): string | null {
  if (!truthPublishBlocked(candidate)) {
    return null;
  }
  if (candidate.commissioner_publish) {
    return candidate.commissioner_publish.reason || candidate.commissioner_publish.headline;
  }
  if (truthLeagueMatchResolution(candidate) === 'cross_league_identity_conflict') {
    return 'Publishing blocked. Teams were detected, but the gamer tags do not match this league’s assigned members.';
  }
  if (truthLeagueMatchResolution(candidate) === 'possible_team_match') {
    if (truthIsEmptyHudHandoffAttestationEligible(candidate)) {
      return EMPTY_HUD_HANDOFF_PUBLISH_BLOCKED;
    }
    return 'Publishing blocked. Teams were detected, but top-HUD gamer tags were not verified against this league’s assigned members.';
  }
  if (
    truthLeagueMatchResolution(candidate) === 'ambiguous' &&
    candidate.competition_match_id &&
    truthHomeAwayConfidence(candidate) === 'high'
  ) {
    const assignedRow = truthAssignedScheduleMatchRow(candidate);
    if (assignedRow && scheduleMatchReversedAgainstVod(candidate, assignedRow)) {
      return 'Publishing blocked. Assigned match home/away conflicts with high-confidence VOD evidence.';
    }
  }
  if (!candidate.truth_checked_at) {
    return 'Publishing blocked. Run Verify With Frames before publishing.';
  }
  return 'Publishing blocked. Frame verification did not confirm this VOD belongs to this league.';
}

export function truthPublishBlockedSeverity(
  candidate: LscVodCandidate
): 'danger' | 'warning' {
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict') {
    return 'danger';
  }
  if (truthGameplayVerificationStatus(candidate) === 'no_gameplay_detected') {
    return 'danger';
  }
  if (resolution === 'conflict') {
    return 'danger';
  }
  if (
    resolution === 'ambiguous' &&
    candidate.competition_match_id &&
    truthHomeAwayConfidence(candidate) === 'high'
  ) {
    const assignedRow = truthAssignedScheduleMatchRow(candidate);
    if (assignedRow && scheduleMatchReversedAgainstVod(candidate, assignedRow)) {
      return 'danger';
    }
  }
  return 'warning';
}

export interface TruthIdentityConflict {
  tag?: string;
  detected_side?: string;
  expected_side?: string;
  current_league_assigned_member?: string | null;
  reason?: string;
}

export function truthIdentityConflicts(candidate: LscVodCandidate): TruthIdentityConflict[] {
  const evidence = truthEvidenceObject(candidate);
  const validation = evidence?.identity_validation;
  if (validation && typeof validation === 'object') {
    const conflicts = (validation as Record<string, unknown>).conflicts;
    if (Array.isArray(conflicts)) {
      return conflicts as TruthIdentityConflict[];
    }
  }
  const conflicts = evidence?.conflicts;
  if (Array.isArray(conflicts)) {
    return conflicts as TruthIdentityConflict[];
  }
  return [];
}

export interface TruthHudTagNormalization {
  raw_tag?: string;
  normalized_tag?: string | null;
  detected_side?: string;
  member_id?: string;
  member_display_name?: string;
  member_status?: string;
  identity_source?: string;
  roster_identity_pending_link?: boolean;
  expected_identity?: string;
  status?: string;
  score?: number;
  reason_code?: string;
}

const HUD_NORMALIZATION_HIGH_CONFIDENCE = new Set([
  'exact_identity_match',
  'normalized_identity_match',
]);

const HUD_NORMALIZATION_LOW_CONFIDENCE = new Set([
  'likely_identity_match',
  'ambiguous_identity_match',
  'identity_unverified',
]);

export function truthHudNormalizationStatusLabel(item: TruthHudTagNormalization): string | null {
  const status = String(item.status ?? '').trim();
  if (!status) {
    return null;
  }
  if (
    item.roster_identity_pending_link &&
    (status === 'exact_identity_match' || status === 'normalized_identity_match')
  ) {
    return 'roster identity matched';
  }
  return status.replace(/_/g, ' ');
}

export function truthHudTagNormalizations(candidate: LscVodCandidate): TruthHudTagNormalization[] {
  const evidence = truthEvidenceObject(candidate);
  const validation = evidence?.identity_validation;
  if (validation && typeof validation === 'object') {
    const fromValidation = (validation as Record<string, unknown>).hud_tag_normalizations;
    if (Array.isArray(fromValidation) && fromValidation.length > 0) {
      return fromValidation as TruthHudTagNormalization[];
    }
  }
  const normalizations = evidence?.hud_tag_normalizations;
  if (!Array.isArray(normalizations)) {
    return [];
  }
  return normalizations as TruthHudTagNormalization[];
}

export function truthHudTagNormalizationSummary(candidate: LscVodCandidate): string | null {
  const normalizations = truthHudTagNormalizations(candidate);
  if (normalizations.length === 0) {
    return null;
  }
  const highConfidence = normalizations.filter((item) =>
    HUD_NORMALIZATION_HIGH_CONFIDENCE.has(String(item.status ?? ''))
  );
  if (highConfidence.length === 0) {
    return null;
  }
  const first = highConfidence[0];
  const raw = String(first.raw_tag ?? '').trim();
  const normalized = String(first.normalized_tag ?? first.expected_identity ?? '').trim();
  const side = String(first.detected_side ?? 'team').trim();
  if (!raw || !normalized) {
    return null;
  }
  return `Roster-aware tag match\n${raw} was interpreted as ${normalized} for ${side}.`;
}

export function truthHasLowConfidenceHudNormalization(candidate: LscVodCandidate): boolean {
  const normalizations = truthHudTagNormalizations(candidate);
  if (normalizations.length === 0) {
    return false;
  }
  return normalizations.some((item) =>
    HUD_NORMALIZATION_LOW_CONFIDENCE.has(String(item.status ?? ''))
  );
}

export function truthIdentityNotVerifiedMessage(candidate: LscVodCandidate): string | null {
  if (truthLeagueMatchResolution(candidate) !== 'possible_team_match') {
    return null;
  }
  if (truthHasLowConfidenceHudNormalization(candidate)) {
    return 'Identity not verified\nTeams were detected, but HUD gamer tags could not be safely matched to assigned owners.';
  }
  return null;
}

export function truthLowConfidenceHudNormalizationMessage(candidate: LscVodCandidate): string | null {
  if (!truthHasLowConfidenceHudNormalization(candidate)) {
    return null;
  }
  return 'Gamer tag needs review\nThe detected HUD tag is close to the assigned owner, but not close enough to verify automatically.';
}

export interface TruthHudCandidateRead {
  frame_index?: number;
  offset_seconds?: number;
  raw_tag?: string;
  normalized_key?: string;
  detected_side?: string;
  confidence?: string;
}

export interface TruthFrameConsensus {
  sample_count?: number;
  usable_gameplay_frames?: number;
  left_hud_candidates?: TruthHudCandidateRead[];
  right_hud_candidates?: TruthHudCandidateRead[];
  left_consensus_tag?: string | null;
  right_consensus_tag?: string | null;
  away_side_label?: string | null;
  home_side_label?: string | null;
  status?: string;
  reason_codes?: string[];
}

export interface TruthVisionSecondPassResult {
  side?: string;
  decision?: string;
  confidence?: string;
  visible_text?: string;
  expected_owner?: string;
  evidence?: string;
}

export interface TruthVisionSecondPass {
  ran?: boolean;
  reason?: string;
  expected_owners?: {
    away?: string;
    home?: string;
  };
  results?: TruthVisionSecondPassResult[];
  reason_codes?: string[];
}

export interface TruthVisionCostDebug {
  consensus_frames_target?: number;
  consensus_frames_used?: number;
  vision_calls?: number;
  primary_vision_calls?: number;
  fallback_vision_calls?: number;
  second_pass_vision_calls?: number;
  preprocessing?: string[];
  truth_cost_estimate?: number;
}

export function truthFrameConsensus(candidate: LscVodCandidate): TruthFrameConsensus | null {
  const evidence = truthEvidenceObject(candidate);
  const consensus = evidence?.frame_consensus;
  if (!consensus || typeof consensus !== 'object') {
    return null;
  }
  return consensus as TruthFrameConsensus;
}

export function truthVisionSecondPass(candidate: LscVodCandidate): TruthVisionSecondPass | null {
  const evidence = truthEvidenceObject(candidate);
  const secondPass = evidence?.vision_second_pass;
  if (!secondPass || typeof secondPass !== 'object') {
    return null;
  }
  return secondPass as TruthVisionSecondPass;
}

export function truthVisionCostDebug(candidate: LscVodCandidate): TruthVisionCostDebug | null {
  const evidence = truthEvidenceObject(candidate);
  const debug = evidence?.debug;
  if (!debug || typeof debug !== 'object') {
    return null;
  }
  const record = debug as Record<string, unknown>;
  const hasCostFields =
    record.consensus_frames_target != null ||
    record.consensus_frames_used != null ||
    record.vision_calls != null;
  if (!hasCostFields) {
    return null;
  }
  return {
    consensus_frames_target:
      record.consensus_frames_target != null
        ? Number(record.consensus_frames_target)
        : undefined,
    consensus_frames_used:
      record.consensus_frames_used != null ? Number(record.consensus_frames_used) : undefined,
    vision_calls: record.vision_calls != null ? Number(record.vision_calls) : undefined,
    primary_vision_calls:
      record.primary_vision_calls != null ? Number(record.primary_vision_calls) : undefined,
    fallback_vision_calls:
      record.fallback_vision_calls != null ? Number(record.fallback_vision_calls) : undefined,
    second_pass_vision_calls:
      record.second_pass_vision_calls != null
        ? Number(record.second_pass_vision_calls)
        : undefined,
    preprocessing: Array.isArray(record.preprocessing)
      ? record.preprocessing.map((value) => String(value)).filter(Boolean)
      : undefined,
    truth_cost_estimate:
      candidate.truth_cost_estimate != null ? Number(candidate.truth_cost_estimate) : undefined,
  };
}

export function truthHudCropPreprocessing(candidate: LscVodCandidate): string[] {
  const debug = truthVisionCostDebug(candidate);
  const preprocessing = debug?.preprocessing;
  if (!Array.isArray(preprocessing)) {
    return [];
  }
  return preprocessing.map((value) => String(value)).filter(Boolean);
}

export function truthVisionSecondPassSummary(candidate: LscVodCandidate): string | null {
  const secondPass = truthVisionSecondPass(candidate);
  if (secondPass?.ran !== true) {
    return null;
  }
  const expectedOwners = secondPass.expected_owners ?? {};
  const results = secondPass.results ?? [];
  const homeResult = results.find((item) => item.side === 'home');
  const awayResult = results.find((item) => item.side === 'away');
  const homeOwner = String(expectedOwners.home ?? '').trim();
  const awayOwner = String(expectedOwners.away ?? '').trim();

  const lines: string[] = ['Roster-constrained Vision recheck'];
  for (const { side, owner, result } of [
    { side: 'Away', owner: awayOwner, result: awayResult },
    { side: 'Home', owner: homeOwner, result: homeResult },
  ]) {
    if (!owner) {
      continue;
    }
    const decision = String(result?.decision ?? 'unclear');
    const confidence = String(result?.confidence ?? 'inconclusive');
    const visible = String(result?.visible_text ?? '').trim();
    if (decision.includes('expected') && confidence === 'high') {
      lines.push(
        `${side} HUD was rechecked against expected ${side.toLowerCase()} owner ${owner} and matched with high confidence.`
      );
    } else {
      lines.push(
        `Expected ${owner} (${side.toLowerCase()} owner). Vision could not verify the HUD tag with enough confidence${
          visible ? ` (read "${visible}")` : ''
        }.`
      );
    }
  }
  return lines.length > 1 ? lines.join('\n') : null;
}

function _uniqueRawTags(candidates: TruthHudCandidateRead[]): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const item of candidates) {
    const tag = String(item.raw_tag ?? '').trim();
    if (!tag || seen.has(tag)) {
      continue;
    }
    seen.add(tag);
    tags.push(tag);
  }
  return tags;
}

export function truthFrameConsensusSummary(candidate: LscVodCandidate): string | null {
  const consensus = truthFrameConsensus(candidate);
  if (!consensus) {
    return null;
  }
  const sampleCount = Number(consensus.sample_count ?? 0);
  const usable = Number(consensus.usable_gameplay_frames ?? 0);
  const leftTag = consensus.left_consensus_tag;
  const rightTag = consensus.right_consensus_tag;
  const leftReads = _uniqueRawTags(consensus.left_hud_candidates ?? []);
  const rightReads = _uniqueRawTags(consensus.right_hud_candidates ?? []);

  const lines = ['Multi-frame HUD consensus'];
  if (leftTag) {
    lines.push(`Left HUD: ${leftTag} from ${usable} of ${sampleCount} gameplay frames.`);
  } else if (leftReads.length > 0) {
    lines.push(`Left HUD: no safe consensus. Reads included ${leftReads.join(', ')}.`);
  }
  if (rightTag) {
    lines.push(`Right HUD: ${rightTag} from ${usable} of ${sampleCount} gameplay frames.`);
  } else if (rightReads.length > 0) {
    lines.push(`Right HUD: no safe consensus. Reads included ${rightReads.join(', ')}.`);
  }
  if (lines.length === 1) {
    return null;
  }
  return lines.join('\n');
}

export function truthHasLowConfidenceFrameConsensus(candidate: LscVodCandidate): boolean {
  const consensus = truthFrameConsensus(candidate);
  if (!consensus) {
    return false;
  }
  const status = String(consensus.status ?? '');
  return status === 'no_consensus' || status === 'partial_consensus';
}

export function truthLeagueMatchBadgeStyle(candidate: LscVodCandidate): string {
  return LEAGUE_MATCH_STYLES[truthLeagueMatchResolution(candidate)];
}

export function truthFrameVerificationBadgeStyle(candidate: LscVodCandidate): string {
  if (!candidate.truth_checked_at) {
    return TRUTH_BADGE_STYLES.unverified;
  }
  const status = truthStatusFromCandidate(candidate);
  return TRUTH_BADGE_STYLES[status];
}

export function possibleTeamMatchSummaryMessage(candidate: LscVodCandidate): string | null {
  if (truthLeagueMatchResolution(candidate) !== 'possible_team_match') {
    return null;
  }
  return (
    truthLeagueResolutionSummary(candidate) ??
    'Detected teams appear in this league, but active top-HUD gamer tags were not verified against assigned members.'
  );
}

export function noLeagueMatchSummaryMessage(candidate: LscVodCandidate): string | null {
  if (truthLeagueMatchResolution(candidate) !== 'no_league_match') {
    return null;
  }
  return (
    truthLeagueResolutionSummary(candidate) ??
    'This VOD appears to contain gameplay, but it does not match any imported league game.'
  );
}

export function truthCanRejectWithoutMatchSuggestion(
  candidate: LscVodCandidate,
  suggestions: Array<{ id?: string }>
): boolean {
  if (truthReviewActionsLocked(candidate)) {
    return false;
  }
  if (suggestions.length > 0) {
    return true;
  }
  return (
    truthLeagueMatchResolution(candidate) === 'no_league_match' ||
    !candidate.competition_match_id
  );
}

export const NO_LEAGUE_MATCH_REVIEW_GUIDANCE =
  'No league match found. You can reject or dismiss this VOD.';

export function matcherUnlikelyWarning(candidate: LscVodCandidate): string | null {
  if (truthLeagueMatchResolution(candidate) !== 'no_league_match') {
    return null;
  }
  return 'Running the matcher is unlikely to find a league match because detected teams do not appear in this league.';
}

export function truthEvidenceSummary(candidate: LscVodCandidate): string | null {
  const evidence = truthEvidenceObject(candidate);
  const summary = (evidence?.evidence_summary as string | undefined)?.trim();
  if (!summary) {
    return null;
  }
  if (summary.length > 240) {
    return `${summary.slice(0, 237)}...`;
  }
  return summary;
}

export interface TruthDebugMetadata {
  sampledTimestamps: number[];
  cropRegions: string[];
  frameAnalysis: Array<Record<string, unknown>>;
  visionModel?: string;
  visionDetail?: string;
}

export function truthDebugMetadata(candidate: LscVodCandidate): TruthDebugMetadata | null {
  const evidence = truthEvidenceObject(candidate);
  const debug = evidence?.debug;
  if (!debug || typeof debug !== 'object') {
    return null;
  }
  const record = debug as Record<string, unknown>;
  const sampledTimestamps = Array.isArray(record.sampled_timestamps)
    ? record.sampled_timestamps.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
    : [];
  const cropRegions = Array.isArray(record.crop_regions)
    ? record.crop_regions.map((value) => String(value))
    : [];
  const frameAnalysis = Array.isArray(record.frame_analysis)
    ? (record.frame_analysis as Array<Record<string, unknown>>)
    : [];
  if (
    sampledTimestamps.length === 0 &&
    cropRegions.length === 0 &&
    frameAnalysis.length === 0
  ) {
    return null;
  }
  return {
    sampledTimestamps,
    cropRegions,
    frameAnalysis,
    visionModel: record.vision_model ? String(record.vision_model) : undefined,
    visionDetail: record.vision_detail ? String(record.vision_detail) : undefined,
  };
}

type OrientedSide = {
  team_abbreviation?: string;
  team_name?: string;
  source?: string;
};

function readOrientedSide(value: unknown): OrientedSide | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  return {
    team_abbreviation: record.team_abbreviation
      ? String(record.team_abbreviation)
      : undefined,
    team_name: record.team_name ? String(record.team_name) : undefined,
    source: record.source ? String(record.source) : undefined,
  };
}

function sideDisplayLabel(side: OrientedSide | null): string | null {
  if (!side) return null;
  return side.team_name || side.team_abbreviation || null;
}

export function truthDetectedAwaySide(candidate: LscVodCandidate): OrientedSide | null {
  const evidence = truthEvidenceObject(candidate);
  const topLevel = readOrientedSide(
    (candidate as unknown as Record<string, unknown>).detected_away_side
  );
  if (topLevel) return topLevel;
  return readOrientedSide(evidence?.detected_away_side);
}

export function truthDetectedHomeSide(candidate: LscVodCandidate): OrientedSide | null {
  const evidence = truthEvidenceObject(candidate);
  const topLevel = readOrientedSide(
    (candidate as unknown as Record<string, unknown>).detected_home_side
  );
  if (topLevel) return topLevel;
  return readOrientedSide(evidence?.detected_home_side);
}

export function truthHomeAwayConfidence(candidate: LscVodCandidate): string | null {
  const evidence = truthEvidenceObject(candidate);
  const value = evidence?.home_away_confidence;
  return typeof value === 'string' ? value : null;
}

export function truthScheduleBinding(candidate: LscVodCandidate): LscTruthScheduleBinding | null {
  const evidence = truthEvidenceObject(candidate);
  const nested = evidence?.schedule_binding;
  if (nested && typeof nested === 'object') {
    return nested as LscTruthScheduleBinding;
  }
  const status = candidate.truth_schedule_binding_status;
  if (!status) {
    return null;
  }
  return {
    schedule_binding_status: status,
    bound_match_id: candidate.truth_bound_match_id,
    timeline_label: candidate.truth_timeline_label ?? undefined,
    binding_reason_codes: candidate.truth_schedule_binding_reason_codes,
  };
}

export function truthScheduleBindingStatus(
  candidate: LscVodCandidate
): LscScheduleBindingStatus | null {
  const binding = truthScheduleBinding(candidate);
  return binding?.schedule_binding_status ?? null;
}

export function truthTimelineLabel(candidate: LscVodCandidate): string | null {
  const binding = truthScheduleBinding(candidate);
  const label = binding?.timeline_label ?? candidate.truth_timeline_label;
  return label?.trim() || null;
}

export function candidatePreviouslyUnpublished(candidate: LscVodCandidate): boolean {
  return candidate.archive_status === 'unpublished';
}

export const PREVIOUSLY_UNPUBLISHED_BANNER_TITLE = 'Previously unpublished';

export const PREVIOUSLY_UNPUBLISHED_BANNER_BODY =
  'This VOD was removed from the archive and can now be reassigned, reviewed, and republished.';

export function truthScheduleCandidateMatches(
  candidate: LscVodCandidate
): LscScheduleBindingCandidateMatch[] {
  const binding = truthScheduleBinding(candidate);
  const matches = binding?.candidate_matches;
  if (!Array.isArray(matches)) {
    return [];
  }
  return matches.filter(
    (match): match is LscScheduleBindingCandidateMatch =>
      Boolean(match && typeof match.match_id === 'string')
  );
}

export function truthPrimaryScheduleMatches(
  candidate: LscVodCandidate
): LscScheduleBindingCandidateMatch[] {
  const binding = truthScheduleBinding(candidate);
  const primary = binding?.primary_matches;
  if (Array.isArray(primary) && primary.length > 0) {
    return primary.filter(
      (match): match is LscScheduleBindingCandidateMatch =>
        Boolean(match && typeof match.match_id === 'string')
    );
  }
  return truthScheduleCandidateMatches(candidate)
    .filter((match) => !match.is_fallback)
    .slice(0, 5);
}

export function truthFallbackScheduleMatches(
  candidate: LscVodCandidate
): LscScheduleBindingCandidateMatch[] {
  const binding = truthScheduleBinding(candidate);
  const fallback = binding?.fallback_matches;
  if (Array.isArray(fallback) && fallback.length > 0) {
    return fallback.filter(
      (match): match is LscScheduleBindingCandidateMatch =>
        Boolean(match && typeof match.match_id === 'string')
    );
  }
  const primaryIds = new Set(
    truthPrimaryScheduleMatches(candidate).map((match) => match.match_id)
  );
  return truthScheduleCandidateMatches(candidate).filter(
    (match) => !primaryIds.has(match.match_id)
  );
}

export function truthScheduleMatchRankLabel(
  match: LscScheduleBindingCandidateMatch
): string | null {
  if (match.rank_reason_codes?.includes('home_away_match')) {
    return 'Home/away matches VOD';
  }
  if (match.is_reversed_orientation) {
    return 'Same teams, reversed home/away — confirm carefully';
  }
  return null;
}

export function truthScheduleMatchWarning(
  match: LscScheduleBindingCandidateMatch
): string | null {
  if (match.is_fallback) {
    return 'This is a lower-confidence schedule match. Confirm carefully before publishing.';
  }
  return null;
}

export function truthScheduleBindingHeadline(candidate: LscVodCandidate): string | null {
  const status = truthScheduleBindingStatus(candidate);
  const timeline = truthTimelineLabel(candidate);
  const away = sideDisplayLabel(truthDetectedAwaySide(candidate));
  const home = sideDisplayLabel(truthDetectedHomeSide(candidate));
  const hasOrientedTeams = Boolean(away && home);

  if (status === 'bound' && timeline) {
    return `Matched to ${timeline}`;
  }
  if (status === 'ambiguous' && candidate.competition_match_id) {
    if (truthAssignedMatchReadyMessage(candidate)) {
      return ASSIGNED_MATCH_READY_MESSAGE;
    }
    return 'Multiple matching games found. Select the correct week.';
  }
  if (status === 'ambiguous') {
    return 'Multiple matching games found. Select the correct week.';
  }
  if (status === 'unbound') {
    if (!hasOrientedTeams) {
      return 'Teams detected, but home/away could not be confirmed. Select manually.';
    }
    return 'No schedule match found from detected away/home.';
  }
  return null;
}

export function truthScheduleBindingGuidance(candidate: LscVodCandidate): string | null {
  const status = truthScheduleBindingStatus(candidate);
  if (status === 'bound') {
    return 'Confirm and publish after review.';
  }
  if (status === 'ambiguous' || status === 'unbound') {
    return 'Manual assignment remains available below.';
  }
  return null;
}

export function formatScheduleMatchup(
  match: Pick<LscScheduleBindingCandidateMatch, 'away_side_label' | 'home_side_label'>
): string {
  return formatAwayAtHome(match);
}

export function formatScheduleCandidateMatch(
  match: Pick<
    LscScheduleBindingCandidateMatch,
    'season_label' | 'stage_label' | 'away_side_label' | 'home_side_label' | 'timeline_label'
  >
): string {
  if (match.timeline_label?.trim()) {
    return match.timeline_label.trim();
  }
  const season = displaySeasonLabel(match.season_label) ?? '';
  const stage = match.stage_label?.trim() ?? '';
  const matchup = formatScheduleMatchup(match);
  return [season, stage, matchup].filter(Boolean).join(' · ');
}

export function formatDetectedVodMatchup(candidate: LscVodCandidate): string | null {
  const away = sideDisplayLabel(truthDetectedAwaySide(candidate));
  const home = sideDisplayLabel(truthDetectedHomeSide(candidate));
  if (!away && !home) {
    return null;
  }
  return `${away ?? '—'} @ ${home ?? '—'}`;
}

export type VodReviewStepKey =
  | 'verify_frames'
  | 'confirm_match'
  | 'resolve_identity'
  | 'empty_hud_handoff_attestation'
  | 'approve'
  | 'publish'
  | 'terminal';

export type VodReviewStepStatus =
  | 'complete'
  | 'current'
  | 'blocked'
  | 'optional'
  | 'not_started';

export type VodReviewStep = {
  key: VodReviewStepKey;
  label: string;
  status: VodReviewStepStatus;
  description?: string;
};

export type VodReviewNextAction = {
  key: VodReviewStepKey;
  title: string;
  description: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  targetAnchor?: string;
  severity: 'info' | 'success' | 'warning' | 'danger';
};

const VOD_REVIEW_STEP_LABELS: Record<Exclude<VodReviewStepKey, 'terminal'>, string> = {
  verify_frames: 'Check scoreboard',
  confirm_match: 'Confirm match',
  resolve_identity: 'Resolve identity',
  empty_hud_handoff_attestation: 'Confirm game',
  approve: 'Approve',
  publish: 'Publish',
};

export function vodReviewHasHomeAwayConflict(candidate: LscVodCandidate): boolean {
  if (truthLeagueMatchResolution(candidate) !== 'ambiguous') {
    return false;
  }
  if (!candidate.competition_match_id) {
    return false;
  }
  if (truthHomeAwayConfidence(candidate) !== 'high') {
    return false;
  }
  const assignedRow = truthAssignedScheduleMatchRow(candidate);
  if (!assignedRow) {
    return false;
  }
  return scheduleMatchReversedAgainstVod(candidate, assignedRow);
}

export function vodReviewScheduleMatchSelectionVisible(candidate: LscVodCandidate): boolean {
  if (truthAssignBlocked(candidate)) {
    return false;
  }
  const status = truthScheduleBindingStatus(candidate);
  if (!status || status === 'not_applicable') {
    return false;
  }
  if (status === 'ambiguous') {
    return true;
  }
  if (status === 'bound' && truthPrimaryScheduleMatches(candidate).length > 0) {
    return true;
  }
  return status === 'unbound';
}

function vodReviewIdentityUnresolved(candidate: LscVodCandidate): boolean {
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict') {
    return false;
  }
  if (truthOverridableIdentityTags(candidate).length > 0) {
    return true;
  }
  if (resolution === 'possible_team_match') {
    if (truthHasActiveEmptyHudHandoffAttestation(candidate)) {
      return false;
    }
    if (truthIsEmptyHudHandoffAttestationEligible(candidate)) {
      return true;
    }
    return !truthIdentitySatisfiedWithCommissionerOverrides(candidate);
  }
  return false;
}

function vodReviewIdentityStepComplete(candidate: LscVodCandidate): boolean {
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'no_league_match') {
    return true;
  }
  if (resolution === 'cross_league_identity_conflict') {
    return false;
  }
  return !vodReviewIdentityUnresolved(candidate);
}

function vodReviewVerifyComplete(candidate: LscVodCandidate): boolean {
  return (
    Boolean(candidate.truth_checked_at) &&
    truthGameplayVerificationStatus(candidate) === 'gameplay_detected'
  );
}

function vodReviewMatchStepComplete(candidate: LscVodCandidate): boolean {
  return Boolean(candidate.competition_match_id) && !vodReviewHasHomeAwayConflict(candidate);
}

function vodReviewMatchStepNeedsAction(candidate: LscVodCandidate): boolean {
  const resolution = truthLeagueMatchResolution(candidate);
  if (vodReviewHasHomeAwayConflict(candidate)) {
    return true;
  }
  if (resolution === 'ambiguous' && !candidate.competition_match_id) {
    return true;
  }
  if (!candidate.competition_match_id && resolution === 'possible_team_match') {
    return true;
  }
  const bindingStatus = truthScheduleBindingStatus(candidate);
  if (
    !candidate.competition_match_id &&
    (bindingStatus === 'ambiguous' || bindingStatus === 'unbound')
  ) {
    return true;
  }
  return false;
}

function vodReviewReadyToApprove(
  candidate: LscVodCandidate,
  suggestions: Array<{ id?: string }>
): boolean {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();
  if (reviewStatus !== 'pending_review') {
    return false;
  }
  if (truthReviewActionsLocked(candidate)) {
    return false;
  }
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict' || resolution === 'no_league_match') {
    return false;
  }
  if (vodReviewMatchStepNeedsAction(candidate) && !vodReviewMatchStepComplete(candidate)) {
    return false;
  }
  if (vodReviewIdentityUnresolved(candidate)) {
    return false;
  }
  if (vodReviewHasHomeAwayConflict(candidate)) {
    return false;
  }
  if (suggestions.length > 0) {
    return true;
  }
  return resolution === 'matched' || resolution === 'likely_match';
}

function vodReviewStepDescription(
  key: Exclude<VodReviewStepKey, 'terminal'>,
  status: VodReviewStepStatus,
  candidate?: LscVodCandidate
): string | undefined {
  if (
    key === 'verify_frames' &&
    candidate &&
    status === 'current' &&
    !candidate.truth_checked_at
  ) {
    const handoffContext = getVerifiedHandoffReviewContext(candidate);
    if (handoffContext) {
      return handoffContext.verifyStepDescription;
    }
  }
  if (status === 'complete') {
    return 'Complete';
  }
  if (status === 'current') {
    return 'Needs action';
  }
  if (status === 'blocked') {
    return 'Blocked';
  }
  if (status === 'optional') {
    return 'Optional';
  }
  return 'Not started';
}

export function getVodReviewProgressSteps(
  candidate: LscVodCandidate,
  suggestions: Array<{ id?: string }> = []
): VodReviewStep[] {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();
  const resolution = truthLeagueMatchResolution(candidate);
  const verifyComplete = vodReviewVerifyComplete(candidate);
  const noGameplay =
    Boolean(candidate.truth_checked_at) &&
    truthGameplayVerificationStatus(candidate) === 'no_gameplay_detected';
  const terminalNoMatch = resolution === 'no_league_match';
  const terminalCrossLeague = resolution === 'cross_league_identity_conflict';
  const matchComplete = vodReviewMatchStepComplete(candidate);
  const matchNeedsAction = vodReviewMatchStepNeedsAction(candidate);
  const identityComplete = vodReviewIdentityStepComplete(candidate);
  const identityUnresolved = vodReviewIdentityUnresolved(candidate);
  const publishBlocked = truthPublishBlocked(candidate);
  const readyToApprove = vodReviewReadyToApprove(candidate, suggestions);

  let verifyStatus: VodReviewStepStatus;
  if (reviewStatus === 'published') {
    verifyStatus = 'complete';
  } else if (!candidate.truth_checked_at) {
    verifyStatus = 'current';
  } else if (noGameplay) {
    verifyStatus = 'blocked';
  } else if (verifyComplete) {
    verifyStatus = 'complete';
  } else {
    verifyStatus = 'blocked';
  }

  let matchStatus: VodReviewStepStatus;
  if (reviewStatus === 'published') {
    matchStatus = 'complete';
  } else if (!verifyComplete) {
    matchStatus = 'not_started';
  } else if (terminalCrossLeague || terminalNoMatch) {
    matchStatus = 'blocked';
  } else if (matchComplete) {
    matchStatus = 'complete';
  } else if (matchNeedsAction || vodReviewHasHomeAwayConflict(candidate)) {
    matchStatus = 'current';
  } else if (resolution === 'matched' || resolution === 'likely_match') {
    matchStatus = 'complete';
  } else {
    matchStatus = 'not_started';
  }

  let identityStatus: VodReviewStepStatus;
  if (reviewStatus === 'published') {
    identityStatus = 'complete';
  } else if (terminalNoMatch) {
    identityStatus = 'optional';
  } else if (!verifyComplete) {
    identityStatus = 'not_started';
  } else if (terminalCrossLeague) {
    identityStatus = 'blocked';
  } else if (identityComplete) {
    identityStatus = 'complete';
  } else if (identityUnresolved) {
    identityStatus = 'current';
  } else {
    identityStatus = 'complete';
  }

  let approveStatus: VodReviewStepStatus;
  if (reviewStatus === 'published' || reviewStatus === 'approved') {
    approveStatus = 'complete';
  } else if (reviewStatus === 'rejected') {
    approveStatus = 'blocked';
  } else if (
    verifyStatus === 'blocked' ||
    matchStatus === 'blocked' ||
    identityStatus === 'blocked'
  ) {
    approveStatus = 'blocked';
  } else if (readyToApprove) {
    approveStatus = 'current';
  } else if (verifyComplete && matchComplete && identityComplete) {
    approveStatus = 'not_started';
  } else {
    approveStatus = 'not_started';
  }

  let publishStatus: VodReviewStepStatus;
  if (reviewStatus === 'published') {
    publishStatus = 'complete';
  } else if (reviewStatus === 'approved') {
    publishStatus = publishBlocked ? 'blocked' : 'current';
  } else {
    publishStatus = 'not_started';
  }

  const stepKeys: Array<Exclude<VodReviewStepKey, 'terminal'>> = [
    'verify_frames',
    'confirm_match',
    'resolve_identity',
    'approve',
    'publish',
  ];

  const statuses: VodReviewStepStatus[] = [
    verifyStatus,
    matchStatus,
    identityStatus,
    approveStatus,
    publishStatus,
  ];

  return stepKeys.map((key, index) => ({
    key,
    label: VOD_REVIEW_STEP_LABELS[key],
    status: statuses[index],
    description: vodReviewStepDescription(key, statuses[index], candidate),
  }));
}

export function getVodReviewNextAction(
  candidate: LscVodCandidate,
  suggestions: Array<{ id?: string }> = []
): VodReviewNextAction | null {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();
  const resolution = truthLeagueMatchResolution(candidate);

  if (reviewStatus === 'published') {
    return {
      key: 'terminal',
      title: 'Published to archive',
      description: 'This VOD is already available in the league archive.',
      severity: 'success',
      targetAnchor: '#review-actions',
    };
  }

  if (reviewStatus === 'dismissed') {
    return {
      key: 'terminal',
      title: 'Dismissed',
      description: 'This candidate was dismissed and is out of the review queue.',
      severity: 'info',
    };
  }

  if (reviewStatus === 'rejected') {
    return {
      key: 'terminal',
      title: 'VOD rejected',
      description:
        'This VOD has been removed from the active review flow. Re-verify if this was a mistake.',
      severity: 'info',
    };
  }

  if (isVodReviewMetadataOnlyUnmatched(candidate)) {
    return {
      key: 'confirm_match',
      title: 'Match or dismiss this synced VOD',
      description:
        'Next: Match to a scheduled game or dismiss if this is not a league VOD. Needs match triage before evidence review.',
      primaryLabel: 'Choose match',
      targetAnchor: '#match-selection',
      severity: 'info',
    };
  }

  if (truthReviewActionsLocked(candidate)) {
    const handoffContext = getVerifiedHandoffReviewContext(candidate);
    if (handoffContext) {
      return {
        key: 'verify_frames',
        title: handoffContext.nextActionTitle,
        description: handoffContext.nextActionDescription,
        primaryLabel: handoffContext.primaryLabel,
        targetAnchor: '#frame-verification',
        severity: 'info',
      };
    }
    return {
      key: 'verify_frames',
      title: 'Check this VOD scoreboard',
      description:
        'Run game verification before assigning, approving, or publishing this VOD.',
      primaryLabel: CHECK_SCOREBOARD_BUTTON_LABEL,
      targetAnchor: '#frame-verification',
      severity: 'info',
    };
  }

  if (resolution === 'cross_league_identity_conflict') {
    return {
      key: 'terminal',
      title: 'Possible wrong league',
      description:
        'The detected players or teams appear to belong to another league. Reject this VOD unless the league assignment is corrected.',
      primaryLabel: 'Reject VOD',
      targetAnchor: '#review-actions',
      severity: 'danger',
    };
  }

  if (resolution === 'no_league_match') {
    return {
      key: 'terminal',
      title: 'No league match found',
      description:
        'This VOD does not match the current league schedule. You can reject or dismiss it.',
      primaryLabel: 'Reject VOD',
      targetAnchor: '#review-actions',
      severity: 'warning',
    };
  }

  if (
    candidate.truth_checked_at &&
    truthGameplayVerificationStatus(candidate) === 'no_gameplay_detected'
  ) {
    return {
      key: 'verify_frames',
      title: 'No gameplay detected',
      description:
        'Game verification did not find active gameplay. Check again if this was a menu/lobby screen, or reject the VOD.',
      primaryLabel: 'Re-verify',
      targetAnchor: '#frame-verification',
      severity: 'warning',
    };
  }

  if (vodReviewHasHomeAwayConflict(candidate)) {
    return {
      key: 'confirm_match',
      title: 'Home/away conflict',
      description:
        'The assigned match conflicts with the home/away teams detected from the VOD. Unassign and choose the correct match.',
      primaryLabel: 'Unassign match',
      targetAnchor: '#review-actions',
      severity: 'danger',
    };
  }

  if (
    resolution === 'ambiguous' &&
    !candidate.competition_match_id
  ) {
    return {
      key: 'confirm_match',
      title: 'Select the correct schedule match',
      description:
        'Multiple league games match this VOD. Choose the correct week/match before approval or publish.',
      primaryLabel: 'Choose match',
      targetAnchor: '#match-selection',
      severity: 'warning',
    };
  }

  if (vodReviewMatchStepNeedsAction(candidate) && !vodReviewMatchStepComplete(candidate)) {
    return {
      key: 'confirm_match',
      title: 'Select the correct schedule match',
      description:
        'Choose the correct league match for this VOD before approval or publish.',
      primaryLabel: 'Choose match',
      targetAnchor: '#match-selection',
      severity: 'warning',
    };
  }

  if (vodReviewIdentityUnresolved(candidate)) {
    const emptyHudEligible = truthIsEmptyHudHandoffAttestationEligible(candidate);
    return {
      key: emptyHudEligible ? 'empty_hud_handoff_attestation' : 'resolve_identity',
      title: emptyHudEligible
        ? CONFIRM_RIGHT_GAME_LABEL
        : 'Link gamertag to roster member',
      description: emptyHudEligible
        ? EMPTY_HUD_NEXT_ACTION_DESCRIPTION
        : 'One or more scoreboard gamertags still need to be matched to a roster member before publishing.',
      primaryLabel: emptyHudEligible
        ? CONFIRM_RIGHT_GAME_LABEL
        : 'Link gamertag to roster member',
      targetAnchor: emptyHudEligible ? '#empty-hud-handoff-attestation' : '#identity-override',
      severity: 'warning',
    };
  }

  if (reviewStatus === 'approved') {
    if (!truthPublishBlocked(candidate)) {
      return {
        key: 'publish',
        title: 'Approved and ready to publish',
        description: 'This VOD is approved and can be published to the league archive.',
        primaryLabel: 'Publish to archive',
        targetAnchor: '#review-actions',
        severity: 'success',
      };
    }

    const blockedMessage = truthPublishBlockedMessage(candidate);
    if (vodReviewIdentityUnresolved(candidate) || resolution === 'possible_team_match') {
      const emptyHudEligible = truthIsEmptyHudHandoffAttestationEligible(candidate);
      return {
        key: emptyHudEligible ? 'empty_hud_handoff_attestation' : 'resolve_identity',
        title: emptyHudEligible
          ? CONFIRM_RIGHT_GAME_LABEL
          : 'Link gamertag to roster member',
        description:
          blockedMessage ??
          (emptyHudEligible
            ? EMPTY_HUD_NEXT_ACTION_DESCRIPTION
            : 'One or more scoreboard gamertags still need to be matched to a roster member before publishing.'),
        primaryLabel: emptyHudEligible
          ? CONFIRM_RIGHT_GAME_LABEL
          : 'Link gamertag to roster member',
        targetAnchor: emptyHudEligible ? '#empty-hud-handoff-attestation' : '#identity-override',
        severity: 'warning',
      };
    }
    if (
      resolution === 'ambiguous' ||
      !candidate.competition_match_id ||
      vodReviewHasHomeAwayConflict(candidate)
    ) {
      return {
        key: 'confirm_match',
        title: 'Confirm the schedule match',
        description:
          blockedMessage ??
          'Choose or confirm the correct league match before publishing.',
        primaryLabel: 'Choose match',
        targetAnchor: '#match-selection',
        severity: 'warning',
      };
    }
    return {
      key: 'publish',
      title: 'Publishing blocked',
      description: blockedMessage ?? 'Publishing is blocked until review issues are resolved.',
      targetAnchor: '#review-actions',
      severity: 'warning',
    };
  }

  if (vodReviewReadyToApprove(candidate, suggestions)) {
    return {
      key: 'approve',
      title: 'Review passed. Approve this match.',
      description:
        'Game verification and match evidence are ready for commissioner approval.',
      primaryLabel: 'Approve match',
      targetAnchor: '#review-actions',
      severity: 'success',
    };
  }

  const blockedMessage = truthPublishBlockedMessage(candidate);
  if (blockedMessage) {
    return {
      key: 'verify_frames',
      title: 'Review in progress',
      description: blockedMessage,
      targetAnchor: '#frame-verification',
      severity: 'info',
    };
  }

  return null;
}

export type VodReviewQueueBucket =
  | 'needs_verification'
  | 'needs_match'
  | 'needs_identity'
  | 'ready_to_approve'
  | 'ready_to_publish'
  | 'no_league_match'
  | 'blocked_conflict'
  | 'published'
  | 'rejected';

export type VodReviewQueueEvidenceTone = 'success' | 'warning' | 'danger' | 'neutral';

export type VodReviewQueueEvidenceChip = {
  key: string;
  label: string;
  tone: VodReviewQueueEvidenceTone;
};

export const VOD_REVIEW_QUEUE_BUCKET_LABELS: Record<VodReviewQueueBucket, string> = {
  needs_verification: 'Needs review',
  needs_match: 'Needs review',
  needs_identity: 'Needs identity or matchup check',
  ready_to_approve: 'Ready to publish',
  ready_to_publish: 'Ready to publish',
  no_league_match: 'No clear league match',
  blocked_conflict: 'Needs commissioner check',
  published: 'Published',
  rejected: 'Removed',
};

export const VOD_REVIEW_QUEUE_BUCKET_DESCRIPTIONS: Record<VodReviewQueueBucket, string> = {
  needs_verification:
    'These games may be ready, but you decide what gets published after checking the evidence.',
  needs_match:
    'Match this stream to a scheduled game, or remove it if it is not league gameplay.',
  needs_identity: 'Confirm which league member streamed this game.',
  ready_to_approve: 'These look ready for your approval.',
  ready_to_publish: 'Approved and ready for you to publish to the archive.',
  no_league_match: 'This stream does not clearly match a league game on the schedule.',
  blocked_conflict: 'Home/away or league identity needs a commissioner decision.',
  published: 'Already published to the league archive.',
  rejected: 'Removed from the active review flow.',
};

export const VOD_REVIEW_QUEUE_BUCKET_ORDER: VodReviewQueueBucket[] = [
  'needs_verification',
  'needs_match',
  'needs_identity',
  'ready_to_approve',
  'ready_to_publish',
  'no_league_match',
  'blocked_conflict',
  'published',
  'rejected',
];

function emptyVodReviewQueueBucketCounts(): Record<VodReviewQueueBucket, number> {
  return VOD_REVIEW_QUEUE_BUCKET_ORDER.reduce(
    (counts, bucket) => {
      counts[bucket] = 0;
      return counts;
    },
    {} as Record<VodReviewQueueBucket, number>
  );
}

function vodReviewIsBlockedConflict(candidate: LscVodCandidate): boolean {
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict') {
    return true;
  }
  if (vodReviewHasHomeAwayConflict(candidate)) {
    return true;
  }
  if (resolution === 'conflict') {
    return true;
  }
  return false;
}

function vodReviewNeedsVerification(candidate: LscVodCandidate): boolean {
  if (isVodReviewMetadataOnlyUnmatched(candidate)) {
    return false;
  }
  if (!candidate.truth_checked_at) {
    return true;
  }
  return truthGameplayVerificationStatus(candidate) === 'no_gameplay_detected';
}

/** Synced inventory metadata with no truth evidence and no schedule match yet. */
export function isVodReviewMetadataOnlyUnmatched(candidate: LscVodCandidate): boolean {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').trim().toLowerCase();
  if (reviewStatus === 'published' || reviewStatus === 'rejected' || reviewStatus === 'dismissed') {
    return false;
  }
  if (candidate.truth_checked_at) {
    return false;
  }
  if (!isVodInventorySyncedUnmatched(candidate)) {
    return false;
  }
  return candidateHasSyncProvenance(candidate);
}

function vodReviewNeedsMatchSelection(candidate: LscVodCandidate): boolean {
  if (vodReviewIsBlockedConflict(candidate)) {
    return false;
  }
  const nextAction = getVodReviewNextAction(candidate);
  if (nextAction?.key === 'confirm_match') {
    return true;
  }
  if (vodReviewMatchStepNeedsAction(candidate) && !vodReviewMatchStepComplete(candidate)) {
    return true;
  }
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'ambiguous' && !candidate.competition_match_id) {
    return true;
  }
  return false;
}

function vodReviewNeedsIdentityReview(candidate: LscVodCandidate): boolean {
  if (vodReviewIsBlockedConflict(candidate)) {
    return false;
  }
  const nextAction = getVodReviewNextAction(candidate);
  if (nextAction?.key === 'resolve_identity') {
    return true;
  }
  if (truthOverridableIdentityTags(candidate).length > 0) {
    return true;
  }
  if (vodReviewIdentityUnresolved(candidate)) {
    return true;
  }
  if (truthIsOverrideEligible(candidate)) {
    return true;
  }
  return false;
}

export function getVodReviewQueueBucket(
  candidate: LscVodCandidate,
  suggestions: Array<{ id?: string }> = []
): VodReviewQueueBucket {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();

  if (reviewStatus === 'published') {
    return 'published';
  }

  if (reviewStatus === 'rejected') {
    return 'rejected';
  }

  if (vodReviewIsBlockedConflict(candidate)) {
    return 'blocked_conflict';
  }

  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'no_league_match') {
    return 'no_league_match';
  }
  if (resolution === 'needs_review') {
    return 'needs_identity';
  }
  if (resolution === 'current_season_schedule_miss') {
    return 'needs_match';
  }

  if (isVodReviewMetadataOnlyUnmatched(candidate)) {
    return 'needs_match';
  }

  if (vodReviewNeedsVerification(candidate)) {
    return 'needs_verification';
  }

  if (reviewStatus === 'approved') {
    if (!truthPublishBlocked(candidate)) {
      return 'ready_to_publish';
    }
    if (vodReviewNeedsIdentityReview(candidate)) {
      return 'needs_identity';
    }
    if (vodReviewNeedsMatchSelection(candidate)) {
      return 'needs_match';
    }
    return 'needs_identity';
  }

  if (vodReviewNeedsMatchSelection(candidate)) {
    return 'needs_match';
  }

  if (vodReviewNeedsIdentityReview(candidate)) {
    return 'needs_identity';
  }

  if (vodReviewReadyToApprove(candidate, suggestions)) {
    return 'ready_to_approve';
  }

  const nextAction = getVodReviewNextAction(candidate, suggestions);
  if (nextAction?.key === 'approve') {
    return 'ready_to_approve';
  }

  if (vodReviewIdentityUnresolved(candidate)) {
    return 'needs_identity';
  }

  if (vodReviewMatchStepNeedsAction(candidate) || !vodReviewMatchStepComplete(candidate)) {
    return 'needs_match';
  }

  return 'ready_to_approve';
}

function gameplayEvidenceChip(candidate: LscVodCandidate): VodReviewQueueEvidenceChip {
  if (!candidate.truth_checked_at) {
    return { key: 'gameplay', label: 'Needs evidence review', tone: 'warning' };
  }
  const gameplayStatus = truthGameplayVerificationStatus(candidate);
  if (gameplayStatus === 'gameplay_detected') {
    return { key: 'gameplay', label: 'Gameplay verified', tone: 'success' };
  }
  if (gameplayStatus === 'no_gameplay_detected') {
    return { key: 'gameplay', label: 'No gameplay detected', tone: 'danger' };
  }
  return { key: 'gameplay', label: 'Verification incomplete', tone: 'warning' };
}

function leagueMatchEvidenceChip(candidate: LscVodCandidate): VodReviewQueueEvidenceChip {
  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict') {
    return { key: 'league_match', label: 'Wrong league', tone: 'danger' };
  }
  if (resolution === 'no_league_match') {
    return { key: 'league_match', label: 'No league match', tone: 'danger' };
  }
  if (resolution === 'needs_review') {
    return {
      key: 'league_match',
      label: LEAGUE_MATCH_LABELS.needs_review,
      tone: 'warning',
    };
  }
  if (resolution === 'current_season_schedule_miss') {
    return {
      key: 'league_match',
      label: LEAGUE_MATCH_LABELS.current_season_schedule_miss,
      tone: 'warning',
    };
  }
  if (resolution === 'ambiguous' || vodReviewNeedsMatchSelection(candidate)) {
    return { key: 'league_match', label: LEAGUE_MATCH_NEEDS_MATCH_CHIP, tone: 'warning' };
  }
  if (
    resolution === 'matched' ||
    resolution === 'likely_match' ||
    candidate.competition_match_id ||
    truthScheduleBindingStatus(candidate) === 'bound'
  ) {
    return { key: 'league_match', label: 'League match found', tone: 'success' };
  }
  return { key: 'league_match', label: 'League match unchecked', tone: 'neutral' };
}

function homeAwayEvidenceChip(candidate: LscVodCandidate): VodReviewQueueEvidenceChip | null {
  if (vodReviewHasHomeAwayConflict(candidate)) {
    return { key: 'home_away', label: 'Home/away conflict', tone: 'danger' };
  }
  if (truthAssignedMatchResolvesScheduleAmbiguity(candidate)) {
    return { key: 'home_away', label: 'Home/away matches VOD', tone: 'success' };
  }
  const detected = formatDetectedVodMatchup(candidate);
  if (!detected) {
    return null;
  }
  if (candidate.competition_match_id && truthHomeAwayConfidence(candidate) === 'high') {
    return { key: 'home_away', label: 'Home/away matches VOD', tone: 'success' };
  }
  return { key: 'home_away', label: 'Home/away unconfirmed', tone: 'warning' };
}

function identityEvidenceChip(candidate: LscVodCandidate): VodReviewQueueEvidenceChip {
  if (truthHasActiveEmptyHudHandoffAttestation(candidate)) {
    return { key: 'identity', label: 'Handoff attested', tone: 'success' };
  }
  if (truthHasCommissionerIdentityOverrides(candidate)) {
    return { key: 'identity', label: 'Commissioner verified', tone: 'success' };
  }
  if (vodReviewIdentityUnresolved(candidate) || truthOverridableIdentityTags(candidate).length > 0) {
    return { key: 'identity', label: 'Identity needs review', tone: 'warning' };
  }
  if (truthPublishIdentityClear(candidate)) {
    return { key: 'identity', label: 'Identity verified', tone: 'success' };
  }
  if (!candidate.truth_checked_at) {
    return { key: 'identity', label: 'Identity not checked', tone: 'neutral' };
  }
  return { key: 'identity', label: 'Identity not required', tone: 'neutral' };
}

function publishEvidenceChip(candidate: LscVodCandidate): VodReviewQueueEvidenceChip | null {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();
  if (reviewStatus === 'published') {
    return { key: 'publish', label: 'Published', tone: 'success' };
  }
  if (reviewStatus === 'approved' && !truthPublishBlocked(candidate)) {
    return { key: 'publish', label: 'Ready to publish', tone: 'success' };
  }
  if (reviewStatus === 'approved' && truthPublishBlocked(candidate)) {
    return { key: 'publish', label: 'Publish blocked', tone: 'danger' };
  }
  if (getVodReviewQueueBucket(candidate) === 'ready_to_approve') {
    return { key: 'publish', label: 'Ready for approval', tone: 'neutral' };
  }
  if (truthPublishBlocked(candidate)) {
    return { key: 'publish', label: 'Publish blocked', tone: 'warning' };
  }
  return null;
}

export function getVodReviewQueueCardEvidence(
  candidate: LscVodCandidate
): VodReviewQueueEvidenceChip[] {
  const chips: VodReviewQueueEvidenceChip[] = [
    gameplayEvidenceChip(candidate),
    leagueMatchEvidenceChip(candidate),
  ];

  const homeAwayChip = homeAwayEvidenceChip(candidate);
  if (homeAwayChip) {
    chips.push(homeAwayChip);
  }

  const identityChip = identityEvidenceChip(candidate);
  if (identityChip.tone !== 'neutral' || candidate.truth_checked_at) {
    chips.push(identityChip);
  }

  const publishChip = publishEvidenceChip(candidate);
  if (publishChip) {
    chips.push(publishChip);
  }

  return chips.slice(0, 5);
}

export function summarizeVodReviewQueueBuckets(
  candidates: LscVodCandidate[],
  suggestionsByCandidateId?: Record<string, Array<{ id?: string }>>
): Record<VodReviewQueueBucket, number> {
  const counts = emptyVodReviewQueueBucketCounts();
  for (const candidate of candidates) {
    const suggestions = suggestionsByCandidateId?.[candidate.id] ?? [];
    const bucket = getVodReviewQueueBucket(candidate, suggestions);
    counts[bucket] += 1;
  }
  return counts;
}

export function isVodCandidateActiveForReviewQueue(candidate: LscVodCandidate): boolean {
  if (isVodCandidateUnavailable(candidate)) {
    return false;
  }
  return String(candidate.candidate_status ?? 'candidate').trim().toLowerCase() === 'candidate';
}

export function isVodReviewQueueActionableBucket(bucket: VodReviewQueueBucket): boolean {
  return bucket !== 'published' && bucket !== 'rejected';
}

export function countActionableVodReviewQueueItems(
  candidates: LscVodCandidate[],
  suggestionsByCandidateId?: Record<string, Array<{ id?: string }>>
): number {
  return candidates.filter((candidate) => {
    if (!isVodCandidateActiveForReviewQueue(candidate)) {
      return false;
    }
    const suggestions = suggestionsByCandidateId?.[candidate.id] ?? [];
    return isVodReviewQueueActionableBucket(getVodReviewQueueBucket(candidate, suggestions));
  }).length;
}

export function filterVodReviewQueueCandidates(
  candidates: LscVodCandidate[],
  filters: {
    review_status?: string;
    bucket?: VodReviewQueueBucket | '';
    broadcaster?: string;
  },
  suggestionsByCandidateId?: Record<string, Array<{ id?: string }>>
): LscVodCandidate[] {
  const broadcasterQuery = filters.broadcaster?.trim().toLowerCase().replace(/^@/, '') ?? '';
  return candidates.filter((candidate) => {
    if (filters.review_status) {
      const status = String(candidate.review_status ?? 'pending_review').toLowerCase();
      if (status !== filters.review_status.toLowerCase()) {
        return false;
      }
    }
    if (filters.bucket) {
      const suggestions = suggestionsByCandidateId?.[candidate.id] ?? [];
      if (getVodReviewQueueBucket(candidate, suggestions) !== filters.bucket) {
        return false;
      }
    }
    if (broadcasterQuery) {
      const login = candidate.provider_broadcaster_login?.toLowerCase() ?? '';
      const display = candidate.provider_broadcaster_display_name?.toLowerCase() ?? '';
      if (!login.includes(broadcasterQuery) && !display.includes(broadcasterQuery)) {
        return false;
      }
    }
    return true;
  });
}

export function sortVodReviewQueueCandidates(candidates: LscVodCandidate[]): LscVodCandidate[] {
  return [...candidates].sort((a, b) => {
    const aTime = Date.parse(a.published_at ?? a.created_at ?? '') || 0;
    const bTime = Date.parse(b.published_at ?? b.created_at ?? '') || 0;
    return bTime - aTime;
  });
}

export function groupVodReviewQueueCandidatesByBucket(
  candidates: LscVodCandidate[],
  suggestionsByCandidateId?: Record<string, Array<{ id?: string }>>
): Record<VodReviewQueueBucket, LscVodCandidate[]> {
  const groups = VOD_REVIEW_QUEUE_BUCKET_ORDER.reduce(
    (acc, bucket) => {
      acc[bucket] = [];
      return acc;
    },
    {} as Record<VodReviewQueueBucket, LscVodCandidate[]>
  );
  for (const candidate of candidates) {
    const suggestions = suggestionsByCandidateId?.[candidate.id] ?? [];
    groups[getVodReviewQueueBucket(candidate, suggestions)].push(candidate);
  }
  return groups;
}

export function parseVodReviewQueueBucketParam(
  value: string | null | undefined
): VodReviewQueueBucket | '' {
  if (!value?.trim()) {
    return '';
  }
  const bucket = value.trim() as VodReviewQueueBucket;
  return VOD_REVIEW_QUEUE_BUCKET_ORDER.includes(bucket) ? bucket : '';
}

export type VodListVerificationTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const VOD_LIST_VERIFICATION_STYLES: Record<VodListVerificationTone, string> = {
  neutral: 'bg-secondary-700 text-secondary-200 border-secondary-600',
  success: 'bg-green-900/50 text-green-200 border-green-700',
  warning: 'bg-amber-900/50 text-amber-200 border-amber-700',
  danger: 'bg-red-900/50 text-red-200 border-red-800',
  info: 'bg-primary-900/40 text-primary-200 border-primary-700',
};

export function metadataConfidenceHint(
  candidate: Pick<LscVodCandidate, 'best_match_confidence_band' | 'match_confidence'>
): string | null {
  const band = candidate.best_match_confidence_band?.trim();
  const score = candidate.match_confidence;
  if (!band && score == null) {
    return null;
  }
  const parts = ['Metadata hint'];
  if (band) {
    parts.push(band);
  }
  if (score != null) {
    parts.push(String(score));
  }
  return parts.join(': ');
}

export function vodListVerificationLabel(candidate: LscVodCandidate): {
  label: string;
  tone: VodListVerificationTone;
  metadataHint: string | null;
} {
  const metadataHint = metadataConfidenceHint(candidate);
  const reviewStatus = String(candidate.review_status ?? 'pending_review').toLowerCase();

  if (reviewStatus === 'published') {
    return { label: 'Published', tone: 'success', metadataHint };
  }
  if (reviewStatus === 'rejected') {
    return { label: 'Rejected', tone: 'neutral', metadataHint };
  }
  if (truthVerificationInProgress(candidate)) {
    return {
      label: VOD_TRUTH_VERIFICATION_QUEUE_LABEL,
      tone: 'info',
      metadataHint: null,
    };
  }
  if (!candidate.truth_checked_at) {
    return { label: 'Not verified', tone: 'neutral', metadataHint };
  }
  if (truthGameplayVerificationStatus(candidate) === 'no_gameplay_detected') {
    return { label: 'No gameplay detected', tone: 'danger', metadataHint };
  }

  const resolution = truthLeagueMatchResolution(candidate);
  if (resolution === 'cross_league_identity_conflict') {
    return { label: 'Wrong league', tone: 'danger', metadataHint };
  }
  if (vodReviewIdentityUnresolved(candidate) || truthOverridableIdentityTags(candidate).length > 0) {
    return { label: 'Identity review needed', tone: 'warning', metadataHint };
  }
  if (resolution === 'no_league_match') {
    return { label: 'No league match', tone: 'warning', metadataHint };
  }
  if (reviewStatus === 'approved') {
    return { label: 'Ready to publish', tone: 'success', metadataHint };
  }
  if (resolution === 'matched' && truthStatusFromCandidate(candidate) === 'verified') {
    return { label: 'Verified', tone: 'success', metadataHint };
  }
  if (
    resolution === 'ambiguous' ||
    resolution === 'possible_team_match' ||
    resolution === 'likely_match' ||
    resolution === 'conflict'
  ) {
    return { label: 'Needs review', tone: 'warning', metadataHint };
  }

  return { label: 'Needs review', tone: 'warning', metadataHint };
}

export function vodListVerificationStyle(tone: VodListVerificationTone): string {
  return VOD_LIST_VERIFICATION_STYLES[tone];
}

function suggestionConfirmationSuffix(suggestion: LscVodMatchSuggestion): string | null {
  const band = suggestion.confidence_band?.trim().toLowerCase();
  if (band === 'low') {
    return 'Needs confirmation';
  }
  if (band && band !== 'high') {
    return 'Metadata suggestion';
  }
  return null;
}

export function formatSuggestionOptionLabel(suggestion: LscVodMatchSuggestion): string {
  const timeline = suggestion.timeline_label?.trim();
  if (timeline) {
    const suffix = suggestionConfirmationSuffix(suggestion);
    return suffix ? `${timeline} · ${suffix}` : timeline;
  }
  const summary = suggestion.match_summary;
  if (summary) {
    const season = displaySeasonLabel(summary.season_label, null, summary.edition) ?? '';
    const stage = summary.stage_label?.trim() ?? '';
    const matchup = formatScheduleMatchup(summary);
    const label = [season, stage, matchup].filter(Boolean).join(' · ');
    if (label) {
      const suffix = suggestionConfirmationSuffix(suggestion);
      return suffix ? `${label} · ${suffix}` : label;
    }
  }
  return suggestion.competition_match_id ?? suggestion.id;
}

export function formatSuggestionOptionTitle(suggestion: LscVodMatchSuggestion): string | null {
  const id = suggestion.competition_match_id ?? suggestion.id;
  const parts = [id];
  if (suggestion.confidence_band) {
    parts.push(`metadata ${suggestion.confidence_band}`);
  }
  if (suggestion.score != null) {
    parts.push(`score ${suggestion.score}`);
  }
  return parts.filter(Boolean).join(' · ') || null;
}

/** VOD inventory page filters — discovery/routing only; no new truth state. */
export const VOD_INVENTORY_SYNC_WINDOW_HOURS = 168;

const VOD_INVENTORY_SYNC_SOURCES = new Set(['manual_sync', 'scheduled_sync', 'admin_sweep']);

export type VodInventoryFilter =
  | 'all'
  | 'sync_activity'
  | 'unmatched'
  | 'pending_review'
  | 'needs_identity'
  | 'ready_to_publish'
  | 'published'
  | 'rejected_dismissed';

export const VOD_INVENTORY_FILTER_ORDER: VodInventoryFilter[] = [
  'all',
  'sync_activity',
  'unmatched',
  'pending_review',
  'needs_identity',
  'ready_to_publish',
  'published',
  'rejected_dismissed',
];

export const VOD_INVENTORY_FILTER_LABELS: Record<VodInventoryFilter, string> = {
  all: 'All',
  sync_activity: 'Sync activity, 7d',
  unmatched: 'Unmatched',
  pending_review: 'Pending review',
  needs_identity: 'Needs identity',
  ready_to_publish: 'Ready to publish',
  published: 'Published',
  rejected_dismissed: 'Rejected / dismissed',
};

export const VOD_INVENTORY_EMPTY_MESSAGES: Record<Exclude<VodInventoryFilter, 'all'>, string> = {
  sync_activity: 'No synced VOD activity in the selected window.',
  unmatched: 'No unmatched VOD candidates found for this league.',
  pending_review: 'No VOD candidates with pending review status.',
  needs_identity: 'No VOD candidates need identity review.',
  ready_to_publish: 'No VOD candidates are ready to publish.',
  published: 'No published VOD candidates found.',
  rejected_dismissed: 'No rejected or dismissed VOD candidates found.',
};

export const VOD_INVENTORY_NOT_VERIFIED_DISCLAIMER =
  'Synced metadata is not verified gameplay.';

export type VodInventorySummary = {
  sync_activity: number;
  unmatched: number;
  pending_review: number;
  needs_identity: number;
  ready_to_publish: number;
  recently_published: number;
  unavailable: number;
};

function vodInventoryReviewStatus(candidate: LscVodCandidate): string {
  return String(candidate.review_status ?? '').trim().toLowerCase();
}

export function isVodInventoryPublished(candidate: LscVodCandidate): boolean {
  const reviewStatus = vodInventoryReviewStatus(candidate);
  if (reviewStatus === 'published') {
    return true;
  }
  return String(candidate.archive_status ?? '').trim().toLowerCase() === 'published';
}

export function candidateHasSyncProvenance(candidate: LscVodCandidate): boolean {
  const syncSource = String(candidate.sync_source ?? '').trim();
  if (syncSource && VOD_INVENTORY_SYNC_SOURCES.has(syncSource)) {
    return true;
  }
  const metadata = candidate.source_metadata ?? {};
  const lastSource = String(metadata.last_sync_source ?? '').trim();
  if (lastSource && VOD_INVENTORY_SYNC_SOURCES.has(lastSource)) {
    return true;
  }
  return Boolean(candidate.sync_run_id || metadata.last_sync_run_id);
}

export function candidateTouchTimestamp(candidate: LscVodCandidate): string | null {
  return candidate.updated_at ?? candidate.created_at ?? null;
}

export function candidateSyncTouchTimestamp(candidate: LscVodCandidate): string | null {
  const metadata = candidate.source_metadata ?? {};
  return candidate.updated_at ?? metadata.last_sync_at ?? candidate.created_at ?? null;
}

function isWithinInventoryWindow(iso: string | null | undefined, windowHours: number): boolean {
  if (!iso) {
    return false;
  }
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return false;
  }
  const cutoff = Date.now() - windowHours * 60 * 60 * 1000;
  return parsed >= cutoff;
}

export function isVodInventorySyncActivity(
  candidate: LscVodCandidate,
  windowHours = VOD_INVENTORY_SYNC_WINDOW_HOURS
): boolean {
  if (isVodCandidateUnavailable(candidate)) {
    return false;
  }
  if (isVodInventoryPublished(candidate)) {
    return false;
  }
  const reviewStatus = vodInventoryReviewStatus(candidate);
  if (reviewStatus === 'rejected' || reviewStatus === 'dismissed') {
    return false;
  }
  if (String(candidate.candidate_status ?? 'candidate').trim().toLowerCase() !== 'candidate') {
    return false;
  }
  if (!candidateHasSyncProvenance(candidate)) {
    return false;
  }
  return isWithinInventoryWindow(candidateTouchTimestamp(candidate), windowHours);
}

export function isVodInventorySyncedUnmatched(candidate: LscVodCandidate): boolean {
  if (isVodCandidateUnavailable(candidate)) {
    return false;
  }
  if (isVodInventoryPublished(candidate)) {
    return false;
  }
  if (candidate.competition_match_id) {
    return false;
  }
  const matchStatus = String(candidate.match_status ?? 'unmatched').trim().toLowerCase();
  return matchStatus === 'unmatched' || matchStatus === 'no_match';
}

export function isVodInventoryPendingReview(candidate: LscVodCandidate): boolean {
  if (isVodCandidateUnavailable(candidate)) {
    return false;
  }
  if (isVodInventoryPublished(candidate)) {
    return false;
  }
  const reviewStatus = vodInventoryReviewStatus(candidate);
  if (reviewStatus === 'rejected' || reviewStatus === 'dismissed') {
    return false;
  }
  return reviewStatus === 'pending_review';
}

export function isVodInventoryNeedsIdentity(candidate: LscVodCandidate): boolean {
  if (isVodCandidateUnavailable(candidate)) {
    return false;
  }
  if (isVodInventoryPublished(candidate)) {
    return false;
  }
  return getVodReviewQueueBucket(candidate) === 'needs_identity';
}

export function isVodInventoryReadyToPublish(candidate: LscVodCandidate): boolean {
  if (isVodCandidateUnavailable(candidate)) {
    return false;
  }
  if (isVodInventoryPublished(candidate)) {
    return false;
  }
  const bucket = getVodReviewQueueBucket(candidate);
  return bucket === 'ready_to_publish' || bucket === 'ready_to_approve';
}

export function isVodInventoryRejectedDismissed(candidate: LscVodCandidate): boolean {
  const reviewStatus = vodInventoryReviewStatus(candidate);
  return reviewStatus === 'rejected' || reviewStatus === 'dismissed';
}

export function matchesVodInventoryFilter(
  candidate: LscVodCandidate,
  filter: VodInventoryFilter,
  windowHours = VOD_INVENTORY_SYNC_WINDOW_HOURS
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'sync_activity':
      return isVodInventorySyncActivity(candidate, windowHours);
    case 'unmatched':
      return isVodInventorySyncedUnmatched(candidate);
    case 'pending_review':
      return isVodInventoryPendingReview(candidate);
    case 'needs_identity':
      return isVodInventoryNeedsIdentity(candidate);
    case 'ready_to_publish':
      return isVodInventoryReadyToPublish(candidate);
    case 'published':
      return isVodInventoryPublished(candidate);
    case 'rejected_dismissed':
      return isVodInventoryRejectedDismissed(candidate);
    default:
      return true;
  }
}

export function filterVodInventoryCandidates(
  candidates: LscVodCandidate[],
  filter: VodInventoryFilter,
  windowHours = VOD_INVENTORY_SYNC_WINDOW_HOURS
): LscVodCandidate[] {
  if (filter === 'all') {
    return candidates;
  }
  return candidates.filter((candidate) => matchesVodInventoryFilter(candidate, filter, windowHours));
}

export function sortVodInventoryCandidatesBySyncTouch(
  candidates: LscVodCandidate[]
): LscVodCandidate[] {
  return [...candidates].sort((a, b) => {
    const aTime =
      Date.parse(candidateSyncTouchTimestamp(a) ?? candidateTouchTimestamp(a) ?? '') || 0;
    const bTime =
      Date.parse(candidateSyncTouchTimestamp(b) ?? candidateTouchTimestamp(b) ?? '') || 0;
    return bTime - aTime;
  });
}

export function summarizeVodInventoryFromCandidates(
  candidates: LscVodCandidate[],
  windowHours = VOD_INVENTORY_SYNC_WINDOW_HOURS
): VodInventorySummary {
  return {
    sync_activity: candidates.filter((c) => isVodInventorySyncActivity(c, windowHours)).length,
    unmatched: candidates.filter(isVodInventorySyncedUnmatched).length,
    pending_review: candidates.filter(isVodInventoryPendingReview).length,
    needs_identity: candidates.filter(isVodInventoryNeedsIdentity).length,
    ready_to_publish: candidates.filter(isVodInventoryReadyToPublish).length,
    recently_published: candidates.filter(isVodInventoryPublished).length,
    unavailable: candidates.filter(isVodCandidateUnavailable).length,
  };
}

export function parseVodInventoryFilterParam(
  filterParam: string | null | undefined,
  matchStatusParam?: string | null
): VodInventoryFilter {
  if (matchStatusParam?.trim().toLowerCase() === 'unmatched') {
    return 'unmatched';
  }
  const value = filterParam?.trim() as VodInventoryFilter;
  return VOD_INVENTORY_FILTER_ORDER.includes(value) ? value : 'all';
}

export function vodInventoryFilterUsesSyncSort(filter: VodInventoryFilter): boolean {
  return filter === 'sync_activity' || filter === 'unmatched';
}

export type VodInventoryDiscoveryBadge = {
  key: string;
  label: string;
  tone: 'neutral' | 'info' | 'warning' | 'success';
};

const VOD_INVENTORY_BADGE_STYLES: Record<VodInventoryDiscoveryBadge['tone'], string> = {
  neutral: 'border-secondary-600 bg-secondary-800/80 text-secondary-200',
  info: 'border-primary-700/60 bg-primary-900/30 text-primary-200',
  warning: 'border-amber-700/60 bg-amber-900/30 text-amber-200',
  success: 'border-green-700/60 bg-green-900/30 text-green-200',
};

export function vodInventoryDiscoveryBadgeStyle(tone: VodInventoryDiscoveryBadge['tone']): string {
  return VOD_INVENTORY_BADGE_STYLES[tone];
}

export function getVodInventoryDiscoveryBadges(
  candidate: LscVodCandidate
): VodInventoryDiscoveryBadge[] {
  const badges: VodInventoryDiscoveryBadge[] = [];
  if (candidateHasSyncProvenance(candidate)) {
    badges.push({ key: 'synced', label: 'Synced', tone: 'info' });
  }
  if (isVodInventorySyncedUnmatched(candidate)) {
    badges.push({ key: 'unmatched', label: 'Unmatched', tone: 'warning' });
  } else if (
    !candidate.competition_match_id &&
    String(candidate.match_status ?? '').trim().toLowerCase() === 'suggested'
  ) {
    badges.push({ key: 'needs_match', label: 'Needs match', tone: 'warning' });
  }
  if (!candidate.truth_checked_at && !isVodInventoryPublished(candidate)) {
    badges.push({ key: 'not_verified', label: 'Not verified', tone: 'neutral' });
  }
  if (isVodInventoryPublished(candidate)) {
    badges.push({ key: 'published', label: 'Published', tone: 'success' });
  } else if (isVodInventoryReadyToPublish(candidate)) {
    badges.push({ key: 'ready', label: 'Ready', tone: 'success' });
  }
  return badges.slice(0, 4);
}
